import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LineChart, Line, ReferenceLine, ReferenceArea } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { syncConstraintsToDB } from '../components/MyPrediction';
import { ExportButton } from '../components/ExportCSV';
import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import FormulaExplainer from '../components/FormulaExplainer';
import ConvergencePanel from '../components/ConvergencePanel';
import BacktestPanel from '../components/BacktestPanel';
import BlindSpotsBanner, { buildTopPicks } from '../components/BlindSpotsBanner';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window14SumRange() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sumMin, setSumMin] = useState(100);
  const [sumMax, setSumMax] = useState(139);
  const [applied, setApplied] = useState(false);
  const [sumSet, setSumSet] = useState(1);
  const [dbSets, setDbSets] = useState({});

  const handleSumSetChange = (s) => {
    setSumSet(s);
    const cs = dbSets[`C${s}`];
    setSumMin(cs?.sum_range_min != null ? cs.sum_range_min : 100);
    setSumMax(cs?.sum_range_max != null ? cs.sum_range_max : 139);
    setApplied(cs?.sum_range_min != null);
  };

  const fetchData = async (showToast) => {
    try {
      const res = await axios.get(`${API}/analysis/sum-range`);
      setData(res.data);
      setLastUpdated(new Date());
      if (showToast) toast.success('Data refreshed');
    } catch { toast.error('Failed to fetch'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(false), 120000);
    return () => clearInterval(interval);
  }, []);
  useDrawsUpdated(() => fetchData(false));

  // Load constraint sets from DB
  useEffect(() => {
    (async () => {
      try {
        const csRes = await axios.get(`${API}/analysis/constraint-sets`);
        const cs = csRes.data || {};
        setDbSets(cs);
        const c1 = cs.C1 || {};
        if (c1.sum_range_min != null) {
          setSumMin(c1.sum_range_min);
          setSumMax(c1.sum_range_max);
          setApplied(true);
        }
      } catch (_e) { /* constraint-sets fetch failed — leave defaults */ }
    })();
  }, []);

  const saveSumRange = () => {
    setApplied(true);
    setDbSets(prev => ({ ...prev, [`C${sumSet}`]: { ...prev[`C${sumSet}`], sum_range_min: parseInt(sumMin), sum_range_max: parseInt(sumMax) } }));
    syncConstraintsToDB(sumSet, { sum_range_min: parseInt(sumMin), sum_range_max: parseInt(sumMax) });
    toast.success(`Sum Range C${sumSet} applied: ${sumMin} - ${sumMax}`);
  };

  const clearConstraint = () => {
    setApplied(false);
    setDbSets(prev => ({ ...prev, [`C${sumSet}`]: { ...prev[`C${sumSet}`], sum_range_min: null, sum_range_max: null } }));
    syncConstraintsToDB(sumSet, { sum_range_min: null, sum_range_max: null });
    toast.info(`Sum Range C${sumSet} cleared`);
  };

  const isInRange = (rangeLabel) => {
    const start = parseInt(rangeLabel.split('-')[0]);
    return start >= Math.floor(sumMin / 10) * 10 && start <= Math.floor(sumMax / 10) * 10;
  };

  // Compute coverage stats for the selected range
  const selectedCoverage = data ? (() => {
    let totalAll = 0, total26 = 0;
    (data.range_comparison || []).forEach(r => {
      const start = parseInt(r.range.split('-')[0]);
      if (start >= Math.floor(sumMin / 10) * 10 && start <= Math.floor(sumMax / 10) * 10) {
        totalAll += r.count_all;
        total26 += r.count_2026;
      }
    });
    return {
      countAll: totalAll,
      pctAll: data.total_draws > 0 ? (totalAll / data.total_draws * 100).toFixed(1) : 0,
      count26: total26,
      pct26: data.total_2026 > 0 ? (total26 / data.total_2026 * 100).toFixed(1) : 0,
    };
  })() : null;

  const maxCount = data ? Math.max(...data.ranges.map(r => r.count)) : 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 14 (B1+B2+B3+B4+B5)</h1><p className="text-xs text-gray-500">Sum Range Analysis</p></div>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && <span className="text-[10px] text-gray-400">Updated: {lastUpdated.toLocaleTimeString()}</span>}
              <Button variant="outline" size="sm" onClick={() => fetchData(true)} data-testid="refresh-btn" className="gap-1 text-xs">
                <CircleNotch size={14} /> Refresh
              </Button>
              {applied && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-1 rounded font-bold">SUM: {sumMin}-{sumMax}</span>}
              <ExportButton data={data} filename="w14_sum_range" />
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W14" {...NARRATIVES.W14} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-white border rounded p-4"><p className="text-xs uppercase font-bold text-gray-500">Avg Sum</p><p className="text-2xl font-bold text-gray-900 mt-1">{data.avg_sum}</p></div>
              <div className="bg-white border rounded p-4"><p className="text-xs uppercase font-bold text-gray-500">Min / Max</p><p className="text-2xl font-bold text-gray-900 mt-1">{data.min_sum} / {data.max_sum}</p></div>
              <div className="bg-white border rounded p-4"><p className="text-xs uppercase font-bold text-gray-500">Std Dev</p><p className="text-2xl font-bold text-gray-900 mt-1">{data.std_dev}</p></div>
              <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200"><p className="text-xs uppercase font-bold text-amber-600">Last 10 Avg</p><p className="text-2xl font-bold text-amber-700 mt-1">{data.last_10_avg}</p></div>
              <div className="bg-white border rounded p-4"><p className="text-xs uppercase font-bold text-gray-500">Total Draws</p><p className="text-lg font-bold text-gray-900 mt-1">{data.total_draws} <span className="text-xs text-gray-400">(2025: {data.total_2025} | 2026: {data.total_2026})</span></p></div>
              <div className="bg-white border rounded p-4"><p className="text-xs uppercase font-bold text-gray-500">Odd/Even Sums</p><p className="text-lg font-bold text-gray-900 mt-1">{data.odd_sums}O / {data.even_sums}E</p></div>
            </div>

            {/* Formula Explainer (collapsible) */}
            <FormulaExplainer context="sum-range" />

            <Tabs defaultValue="consolidated" className="space-y-4">
              <TabsList className="flex flex-wrap w-full h-auto gap-1 [&>button]:shrink-0" data-testid="w14-tabs-list">
                <TabsTrigger value="consolidated" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white font-bold" data-testid="tab-consolidated">🏆 Consolidated</TabsTrigger>
                <TabsTrigger value="convergence" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-convergence">🎯 Convergence</TabsTrigger>
                <TabsTrigger value="predictions" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-bold" data-testid="tab-predictions">Formula 1 (Long-term) <span className="text-[10px] font-normal opacity-70">(2 blocks)</span></TabsTrigger>
                <TabsTrigger value="formula2" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white font-bold" data-testid="tab-formula2">Formula 2 (Next-draw)</TabsTrigger>
                <TabsTrigger value="transitions" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-transitions">Before & After <span className="text-[10px] font-normal opacity-70">(6 blocks)</span></TabsTrigger>
                <TabsTrigger value="comparison" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-comparison">2025 vs 2026 <span className="text-[10px] font-normal opacity-70">(2 blocks)</span></TabsTrigger>
                <TabsTrigger value="droughts" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white font-bold" data-testid="tab-droughts">Droughts <span className="text-[10px] font-normal opacity-70">(2 blocks)</span></TabsTrigger>
                <TabsTrigger value="distribution" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-distribution">Distribution <span className="text-[10px] font-normal opacity-70">(2 blocks)</span></TabsTrigger>
                <TabsTrigger value="repeats" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-repeats">Repeats <span className="text-[10px] font-normal opacity-70">(3 blocks)</span></TabsTrigger>
                <TabsTrigger value="recent" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-recent">2026 Draws <span className="text-[10px] font-normal opacity-70">(1 block)</span></TabsTrigger>
              </TabsList>

              {/* === CONSOLIDATED TAB (Master Verdict across all sub-windows) === */}
              <TabsContent value="consolidated">
                <W2ConsolidatedVerdict
                  data={data}
                  sumSet={sumSet}
                  handleSumSetChange={handleSumSetChange}
                  dbSets={dbSets}
                  applied={applied}
                  sumMin={sumMin}
                  setSumMin={setSumMin}
                  sumMax={sumMax}
                  setSumMax={setSumMax}
                  setApplied={setApplied}
                  saveSumRange={saveSumRange}
                  clearConstraint={clearConstraint}
                  selectedCoverage={selectedCoverage}
                  isInRange={isInRange}
                />
              </TabsContent>

              {/* === CONVERGENCE TAB === */}
              <TabsContent value="convergence">
                <div className="space-y-5">
                  <BacktestPanel endpoint="sum-range" />
                  <BlindSpotsBanner
                    riskFlags={data.risk_flags}
                    topPicks={buildTopPicks(data.convergence)}
                    itemLabel="ranges"
                    renderItem={(rng) => <span className="font-mono font-bold tracking-wider">{rng}</span>}
                  />
                  <ConvergencePanel
                    convergence={data.convergence}
                    itemLabel="Range"
                    renderItem={(rng) => <span className="font-mono font-bold tracking-wider">{rng}</span>}
                    f1Lookup={(rng) => {
                      const m = (data.range_predictions || []).find(p => p.range === rng);
                      return m ? { score: m.pred_score, score_v_adj: m.pred_score_volatility_adj } : null;
                    }}
                    f2Lookup={(rng) => {
                      const m = (data.range_predictions || []).find(p => p.range === rng);
                      return m ? { score: m.combined_score_v2 } : null;
                    }}
                  />
                </div>
              </TabsContent>

              {/* === FORMULA 2 TAB (Next-draw) === */}
              <TabsContent value="formula2">
                <Formula2RangesTab data={data} />
              </TabsContent>


              {/* === PREDICTION TAB === */}
              <TabsContent value="predictions">
                <div className="space-y-6">
                  {/* Top Predicted Ranges */}
                  <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-6 text-white">
                    <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl" data-testid="pred-heading">Block 1 — Predicted Next Sum Range</h3></div>
                    <p className="text-xs text-white/70 mb-4">Score = Frequency% x Overdue Ratio^1.3 | Last draw sum: {data.last_10?.[data.last_10.length - 1]?.sum} (Range: {data.transition?.last_range})</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {(data.range_predictions || []).slice(0, 5).map((p, i) => (
                        <div key={p.range} className={`p-4 rounded-lg ${i === 0 ? 'bg-amber-400 text-amber-900 ring-2 ring-amber-300' : 'bg-white/15'}`} data-testid={`pred-range-${i}`}>
                          <div className="text-[10px] uppercase font-bold mb-1 opacity-80">#{i + 1}</div>
                          <div className="text-2xl font-mono font-bold text-center">{p.range}</div>
                          <div className="text-xs text-center mt-1 opacity-90">Score: {p.pred_score}</div>
                          <div className="text-[10px] text-center mt-1 opacity-70">Freq: {p.freq_pct}% | Gap: {p.current_gap} | Overdue: {p.overdue_ratio}x</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Full Prediction Table */}
                  <div className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-1">Block 2 — All Ranges Ranked by Prediction Score</h3>
                    <p className="text-xs text-gray-500 mb-3">Score = Freq% x (Current Gap / Expected Gap)^1.3</p>
                    <div className="overflow-x-auto" data-testid="pred-table">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs uppercase">#</th>
                            <th className="px-3 py-2 text-left text-xs uppercase">Range</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Count</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Freq %</th>
                            <th className="px-3 py-2 text-center text-xs uppercase bg-blue-50">2026 Count</th>
                            <th className="px-3 py-2 text-center text-xs uppercase bg-blue-50">Diff %</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Exp Gap</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Current Gap</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Overdue</th>
                            <th className="px-3 py-2 text-center text-xs uppercase bg-amber-50 font-bold">Score</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">In 2026</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const rp = [...(data.range_predictions || [])];
                            // Compute top-2 distinct scores (ties share the top slot)
                            const distinctScores = [...new Set(rp.map(r => r.pred_score))].sort((a, b) => b - a);
                            const top2Threshold = distinctScores.length >= 2 ? distinctScores[1] : (distinctScores[0] || 0);
                            return rp.sort((a, b) => (b.count || 0) - (a.count || 0)).map((p, idx) => {
                              const inZone = isInRange(p.range);
                              const isTopScore = p.pred_score >= top2Threshold && top2Threshold > 0;
                              return (
                              <tr key={p.range} className={`${idx < 5 ? 'bg-amber-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${inZone ? 'font-bold' : 'font-normal'}`} style={inZone && idx < 5 ? { borderLeft: '3px solid #f59e0b' } : inZone ? { borderLeft: '3px solid #10b981' } : {}}>
                                <td className="px-3 py-2 border-b font-mono text-center text-gray-400">{idx + 1}</td>
                                <td className="px-3 py-2 border-b font-mono font-bold text-lg">{p.range} {inZone && <span className="text-[9px] text-emerald-600 ml-1">IN ZONE</span>}</td>
                                <td className="px-3 py-2 border-b font-mono text-center">{p.count}</td>
                                <td className="px-3 py-2 border-b font-mono text-center">{p.freq_pct}%</td>
                                <td className="px-3 py-2 border-b font-mono text-center font-bold bg-blue-50/40">{p.count_2026 || 0}</td>
                                {(() => {
                                  const diff = Math.round(((p.pct_2026 || 0) - (p.freq_pct || 0)) * 10) / 10;
                                  const cls = diff > 1 ? 'text-emerald-700 bg-emerald-50' : diff < -1 ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-50';
                                  return (
                                    <td className={`px-3 py-2 border-b font-mono text-center font-bold ${cls}`}>
                                      {diff > 0 ? '+' : ''}{diff}%
                                    </td>
                                  );
                                })()}
                                <td className="px-3 py-2 border-b font-mono text-center text-gray-500">{p.expected_gap}</td>
                                <td className={`px-3 py-2 border-b font-mono text-center font-bold ${p.overdue_ratio >= 2 ? 'text-red-600' : p.overdue_ratio >= 1 ? 'text-amber-600' : 'text-gray-600'}`}>{p.current_gap}</td>
                                <td className={`px-3 py-2 border-b font-mono text-center font-bold ${p.overdue_ratio >= 2 ? 'text-red-600 bg-red-50' : p.overdue_ratio >= 1 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600'}`}>{p.overdue_ratio}x</td>
                                <td className="px-3 py-2 border-b text-center" data-testid={`score-cell-${p.range}`}>
                                  {isTopScore ? (
                                    <span className="inline-flex w-11 h-11 rounded-full items-center justify-center bg-emerald-500 text-white font-mono font-black text-sm shadow-md ring-2 ring-emerald-200">
                                      {p.pred_score}
                                    </span>
                                  ) : (
                                    <span className="font-mono font-bold text-lg text-[#002FA7]">{p.pred_score}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 border-b text-center">
                                  {p.count_2026 > 0
                                    ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">{p.count_2026}x ({p.pct_2026}%)</span>
                                    : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">MISSING</span>}
                                </td>
                              </tr>
                            )});
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* === BEFORE & AFTER TAB === */}
              <TabsContent value="transitions">
                <div className="space-y-6">
                  {/* Current state */}
                  <div className="bg-gradient-to-r from-purple-700 to-violet-800 rounded-lg p-5 text-white">
                    <h3 className="font-bold text-lg mb-3">Block 1 — Current Sum Range Sequence</h3>
                    <div className="flex items-center gap-3 text-center">
                      <div className="bg-white/15 rounded-lg p-3 flex-1">
                        <div className="text-[10px] uppercase opacity-70 mb-1">2nd Last</div>
                        <div className="text-xl font-mono font-bold">{data.transition?.second_last_range || '-'}</div>
                      </div>
                      <ArrowDown size={20} className="rotate-[-90deg] opacity-50" />
                      <div className="bg-white/15 rounded-lg p-3 flex-1">
                        <div className="text-[10px] uppercase opacity-70 mb-1">Last Draw</div>
                        <div className="text-xl font-mono font-bold">{data.transition?.last_range || '-'}</div>
                      </div>
                      <ArrowDown size={20} className="rotate-[-90deg] opacity-50" />
                      <div className="bg-amber-400 text-amber-900 rounded-lg p-3 flex-1 ring-2 ring-amber-300">
                        <div className="text-[10px] uppercase font-bold mb-1">Next?</div>
                        <div className="text-xl font-mono font-bold">?</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* 1st Order: After last range */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1" data-testid="after-1st-heading">Block 2 — After &quot;{data.transition?.last_range}&quot;</h3>
                      <p className="text-xs text-gray-500 mb-3">1st order: What range historically follows {data.transition?.last_range}?</p>
                      <table className="w-full text-sm" data-testid="after-1st-table">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs uppercase">Next Range</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Count</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">%</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Bar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.transition?.after_1st_order || []).map((t, i) => (
                            <tr key={t.range} className={i === 0 ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={i === 0 ? { borderLeft: '3px solid #f59e0b' } : {}}>
                              <td className="px-3 py-2 border-b font-mono font-bold">{t.range}</td>
                              <td className="px-3 py-2 border-b font-mono text-center">{t.count}</td>
                              <td className="px-3 py-2 border-b font-mono text-center font-bold">{t.pct}%</td>
                              <td className="px-3 py-2 border-b">
                                <div className="w-full bg-gray-100 rounded h-3">
                                  <div className="h-full bg-[#002FA7] rounded" style={{ width: `${t.pct}%` }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* 2nd Order: After pair */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1" data-testid="after-2nd-heading">Block 3 — After &quot;{data.transition?.last_pair}&quot;</h3>
                      <p className="text-xs text-gray-500 mb-3">2nd order: After the specific sequence of two ranges, what came next?</p>
                      <table className="w-full text-sm" data-testid="after-2nd-table">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs uppercase">Next Range</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Count</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">%</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Bar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.transition?.after_2nd_order || []).map((t, i) => (
                            <tr key={t.range} className={i === 0 ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={i === 0 ? { borderLeft: '3px solid #f59e0b' } : {}}>
                              <td className="px-3 py-2 border-b font-mono font-bold">{t.range}</td>
                              <td className="px-3 py-2 border-b font-mono text-center">{t.count}</td>
                              <td className="px-3 py-2 border-b font-mono text-center font-bold">{t.pct}%</td>
                              <td className="px-3 py-2 border-b">
                                <div className="w-full bg-gray-100 rounded h-3">
                                  <div className="h-full bg-violet-600 rounded" style={{ width: `${t.pct}%` }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Before last */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1" data-testid="before-heading">Block 4 — Before &quot;{data.transition?.last_range}&quot;</h3>
                      <p className="text-xs text-gray-500 mb-3">What ranges typically appear before {data.transition?.last_range}?</p>
                      <table className="w-full text-sm" data-testid="before-table">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs uppercase">Prev Range</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Count</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">%</th>
                            <th className="px-3 py-2 text-center text-xs uppercase">Bar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.transition?.before_last || []).map((t, i) => (
                            <tr key={t.range} className={i === 0 ? 'bg-blue-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={i === 0 ? { borderLeft: '3px solid #3b82f6' } : {}}>
                              <td className="px-3 py-2 border-b font-mono font-bold">{t.range}</td>
                              <td className="px-3 py-2 border-b font-mono text-center">{t.count}</td>
                              <td className="px-3 py-2 border-b font-mono text-center font-bold">{t.pct}%</td>
                              <td className="px-3 py-2 border-b">
                                <div className="w-full bg-gray-100 rounded h-3">
                                  <div className="h-full bg-blue-500 rounded" style={{ width: `${t.pct}%` }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Convergence insight */}
                  {data.transition?.after_1st_order?.length > 0 && data.transition?.after_2nd_order?.length > 0 && (
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-2">Block 5 — Convergence: Where Both Orders Agree</h3>
                      <p className="text-xs text-gray-500 mb-3">Ranges appearing in both 1st order (after {data.transition?.last_range}) and 2nd order (after {data.transition?.last_pair}) top results</p>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const first5 = (data.transition?.after_1st_order || []).slice(0, 5).map(t => t.range);
                          const second5 = (data.transition?.after_2nd_order || []).slice(0, 5).map(t => t.range);
                          const both = first5.filter(r => second5.includes(r));
                          const only1st = first5.filter(r => !second5.includes(r));
                          const only2nd = second5.filter(r => !first5.includes(r));
                          return (
                            <>
                              {both.map(r => (
                                <span key={r} className="px-3 py-1.5 rounded-full font-mono font-bold text-sm bg-emerald-100 text-emerald-800 ring-2 ring-emerald-300">
                                  {r} (Both)
                                </span>
                              ))}
                              {only1st.map(r => (
                                <span key={r} className="px-3 py-1.5 rounded-full font-mono font-bold text-xs bg-blue-50 text-blue-700">
                                  {r} (1st only)
                                </span>
                              ))}
                              {only2nd.map(r => (
                                <span key={r} className="px-3 py-1.5 rounded-full font-mono font-bold text-xs bg-violet-50 text-violet-700">
                                  {r} (2nd only)
                                </span>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Recovery Patterns: After big drop — ADAPTIVE to current draw */}
                  {data.recovery_pattern_meta && (
                    <div className="bg-white border rounded p-4">
                      {(() => {
                        const meta = data.recovery_pattern_meta;
                        const matches = data.recovery_pattern_total_matches || 0;
                        const pats = data.recovery_patterns || [];

                        // Aggregate next-1 stats from historical matches
                        let recovered = 0, stayed_low = 0, dropped_further = 0;
                        const recoverThresh = parseInt((meta.from_range || '0-0').split('-')[0], 10) || 140;
                        const lowThresh = parseInt((meta.to_range || '0-0').split('-')[1], 10) || 99;
                        for (const rp of pats) {
                          if (rp.next1.sum >= recoverThresh - 20) recovered += 1;
                          else if (rp.next1.sum <= lowThresh) stayed_low += 1;
                          else dropped_further += 1;
                        }
                        const total = pats.length || 1;
                        const recoveredPct = Math.round(recovered / total * 100);
                        const stayedLowPct = Math.round(stayed_low / total * 100);
                        const droppedPct = 100 - recoveredPct - stayedLowPct;

                        return (
                          <>
                            <h3 className="font-bold text-gray-900 mb-1" data-testid="recovery-heading">
                              Block 6 — Recovery After Drop ({meta.from_range} → {meta.to_range})
                            </h3>
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg p-3 mb-3" data-testid="recovery-current-context">
                              <div className="text-xs text-amber-900">
                                <strong>Current draw:</strong> sum dropped from <strong className="text-emerald-700 font-mono">{meta.before.sum}</strong> ([{meta.from_range}]) to <strong className="text-red-700 font-mono">{meta.drop.sum}</strong> ([{meta.to_range}]) — a drop of <strong>{meta.drop_magnitude} points</strong>. This block searches history for the SAME from→to band transition.
                              </div>
                              <div className="mt-2 text-xs text-amber-800">
                                <strong>{matches}</strong> historical occurrence{matches === 1 ? '' : 's'} found. Showing the most recent {pats.length}.
                              </div>
                            </div>
                            {pats.length > 0 ? (
                              <>
                                <p className="text-xs text-gray-500 mb-3">Historical pattern: when sum drops from <span className="font-mono font-semibold">{meta.from_range}</span> to <span className="font-mono font-semibold">{meta.to_range}</span>, what happens in the next 3 draws?</p>
                                <div className="grid grid-cols-3 gap-2 mb-3" data-testid="recovery-summary-stats">
                                  <div className="rounded-lg p-2 bg-emerald-50 border border-emerald-200 text-center">
                                    <div className="text-[10px] text-emerald-700 uppercase tracking-wider font-bold">Recovered next draw</div>
                                    <div className="text-lg font-mono font-black text-emerald-800">{recoveredPct}%</div>
                                    <div className="text-[9px] text-emerald-600">{recovered}/{total} climbed back near {meta.from_range}</div>
                                  </div>
                                  <div className="rounded-lg p-2 bg-amber-50 border border-amber-200 text-center">
                                    <div className="text-[10px] text-amber-700 uppercase tracking-wider font-bold">Stayed low</div>
                                    <div className="text-lg font-mono font-black text-amber-800">{stayedLowPct}%</div>
                                    <div className="text-[9px] text-amber-600">{stayed_low}/{total} stayed at/below {meta.to_range}</div>
                                  </div>
                                  <div className="rounded-lg p-2 bg-rose-50 border border-rose-200 text-center">
                                    <div className="text-[10px] text-rose-700 uppercase tracking-wider font-bold">Mid-band</div>
                                    <div className="text-lg font-mono font-black text-rose-800">{droppedPct}%</div>
                                    <div className="text-[9px] text-rose-600">{dropped_further}/{total} settled in between</div>
                                  </div>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs" data-testid="recovery-table">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-2 py-2 text-center uppercase">Before ({meta.from_range})</th>
                                        <th className="px-2 py-2 text-center uppercase text-red-600">Drop ({meta.to_range})</th>
                                        <th className="px-1 py-2"></th>
                                        <th className="px-2 py-2 text-center uppercase bg-blue-50">Next 1</th>
                                        <th className="px-2 py-2 text-center uppercase bg-blue-50">Next 2</th>
                                        <th className="px-2 py-2 text-center uppercase bg-blue-50">Next 3</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {pats.map((rp, i) => (
                                        <tr key={`rp-${rp.before.sum}-${rp.drop.sum}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                          <td className="px-2 py-1.5 border-b font-mono text-center font-bold text-emerald-700">{rp.before.sum}<span className="text-gray-400 text-[9px] ml-0.5">[{rp.before.range}]</span></td>
                                          <td className="px-2 py-1.5 border-b font-mono text-center font-bold text-red-600 bg-red-50/50">{rp.drop.sum}<span className="text-gray-400 text-[9px] ml-0.5">[{rp.drop.range}]</span></td>
                                          <td className="px-1 py-1.5 border-b text-gray-300">→</td>
                                          <td className="px-2 py-1.5 border-b font-mono text-center font-bold bg-blue-50/30">{rp.next1.sum}<span className="text-gray-400 text-[9px] ml-0.5">[{rp.next1.range}]</span></td>
                                          <td className="px-2 py-1.5 border-b font-mono text-center bg-blue-50/20">{rp.next2.sum}<span className="text-gray-400 text-[9px] ml-0.5">[{rp.next2.range}]</span></td>
                                          <td className="px-2 py-1.5 border-b font-mono text-center bg-blue-50/10">{rp.next3.sum}<span className="text-gray-400 text-[9px] ml-0.5">[{rp.next3.range}]</span></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-gray-500 italic mt-2">
                                No historical occurrences of this exact band-transition. The current drop ({meta.from_range} → {meta.to_range}) is rare or unprecedented in the dataset.
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {!data.recovery_pattern_meta && (
                    <div className="bg-white border rounded p-4 text-xs text-gray-500 italic" data-testid="recovery-no-drop">
                      Block 6 — Recovery After Big Drop: <strong>not applicable</strong> for this draw. The most recent draw did not show a meaningful sum drop (≥30 points and crossing into a different band).
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* === 2025 vs 2026 TAB === */}
              <TabsContent value="comparison">
                <div className="space-y-6">
                  {/* Block 1 — Line Graph: How 2026 sums move between peaks/troughs */}
                  <div className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-1" data-testid="line-heading">Block 1 — 2026 Sum Trajectory (Line Graph)</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Tracks how the sum moves draw-by-draw in 2026. Each point = one draw. Spot whether sums climb gradually (e.g. 100 → 120 → 140 → 180) or jump abruptly. Reference bands at 100 and 150 mark normal/high boundaries; the dashed line shows the historical average ({data.avg_sum}).
                    </p>
                    <div style={{ width: '100%', height: 380 }} data-testid="line-graph">
                      <ResponsiveContainer>
                        <LineChart
                          data={(data.sums_2026_wave || []).map((s, i) => ({
                            idx: i + 1,
                            date: s.draw_date,
                            sum: s.sum,
                            range: s.range,
                          }))}
                          margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} angle={-45} textAnchor="end" height={70} interval={0} />
                          <YAxis tick={{ fontSize: 11, fill: '#475569' }} domain={[40, 240]} ticks={[40, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240]} />
                          <Tooltip
                            contentStyle={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                            formatter={(value, _name, props) => [`Sum ${value} · range ${props.payload.range}`, props.payload.date]}
                          />
                          <ReferenceArea y1={40} y2={100} fill="#fee2e2" fillOpacity={0.4} label={{ value: 'Low (<100)', position: 'insideLeft', fontSize: 10, fill: '#b91c1c' }} />
                          <ReferenceArea y1={100} y2={150} fill="#dbeafe" fillOpacity={0.3} label={{ value: 'Normal (100-149)', position: 'insideLeft', fontSize: 10, fill: '#1e40af' }} />
                          <ReferenceArea y1={150} y2={240} fill="#d1fae5" fillOpacity={0.4} label={{ value: 'High (≥150)', position: 'insideLeft', fontSize: 10, fill: '#065f46' }} />
                          <ReferenceLine y={data.avg_sum} stroke="#dc2626" strokeDasharray="5 4" label={{ value: `Avg ${data.avg_sum}`, fill: '#dc2626', fontSize: 11, position: 'right' }} />
                          <Line type="monotone" dataKey="sum" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 4, fill: '#002FA7' }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 text-[10px] text-gray-500 italic">
                      <b>Reading:</b> The connecting line between dots reveals the climb/drop pattern. Sharp diagonals = abrupt jumps; gentle slopes = gradual stepping. If the line bypasses bands (e.g. jumps directly from 80 → 170 without touching 100s/120s), that&apos;s a &quot;shock move&quot;.
                    </div>

                    {/* Streak analysis panel — below the line graph, powered by 22-year baseline */}
                    {data.streak_history && (
                      <StreakPanel
                        lineStreak={{
                          avg: data.streak_history.avg_reference,
                          curSide: data.streak_history.current?.side,
                          curLen: data.streak_history.current?.len || 0,
                          curEndSum: data.streak_history.current?.end_sum,
                          curEndDate: data.streak_history.current?.end_date,
                          maxAbove: data.streak_history.all_time_max?.above || 0,
                          maxBelow: data.streak_history.all_time_max?.below || 0,
                          baselines: data.streak_history.baselines || {},
                          currentYear: data.streak_history.current_year || {},
                          nextSideHint: (() => {
                            const s = data.streak_history;
                            if (!s?.current?.side) return null;
                            const limit = s.current.side === 'above' ? (s.all_time_max?.above || 0) : (s.all_time_max?.below || 0);
                            return s.current.len >= limit ? (s.current.side === 'above' ? 'below' : 'above') : s.current.side;
                          })(),
                        }}
                        compact={false}
                      />
                    )}

                    {/* Deep Analysis — Bounce/Below × dual-formula × missing ranges */}
                    {data.streak_deep_analysis && (
                      <StreakDeepAnalysis sda={data.streak_deep_analysis} />
                    )}
                  </div>

                  {/* Block 2 — Column Graph: 2025 vs 2026 paired by draw position */}
                  <div className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-1" data-testid="paired-heading">Block 2 — 2025 vs 2026 Date-Wise Comparison (Column Graph)</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Side-by-side sum comparison. The Nth draw of 2026 is placed next to the Nth draw of 2025. Both dates shown on hover to spot patterns where 2026 systematically runs higher/lower than the same point a year earlier.
                    </p>
                    {(() => {
                      const wave2026 = data.sums_2026_wave || [];
                      const wave2025 = data.sums_2025_wave || [];
                      const paired = wave2026.map((d26, i) => ({
                        idx: i + 1,
                        date_2026: d26.draw_date,
                        date_2025: wave2025[i]?.draw_date || '—',
                        '2026': d26.sum,
                        '2025': wave2025[i]?.sum ?? null,
                        diff: wave2025[i] ? d26.sum - wave2025[i].sum : null,
                      }));
                      const avg2026 = paired.length ? Math.round(paired.reduce((s, x) => s + x['2026'], 0) / paired.length) : 0;
                      const avg2025 = paired.length ? Math.round(paired.filter(x => x['2025'] !== null).reduce((s, x) => s + x['2025'], 0) / paired.filter(x => x['2025'] !== null).length) : 0;
                      const higher = paired.filter(p => p.diff !== null && p.diff > 0).length;
                      const lower = paired.filter(p => p.diff !== null && p.diff < 0).length;
                      return (
                        <>
                          <div className="flex flex-wrap gap-3 mb-3 text-[11px]">
                            <span className="px-2 py-1 rounded bg-blue-50 border border-blue-200"><b className="text-blue-700">2025 avg (first {paired.length} draws):</b> {avg2025}</span>
                            <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200"><b className="text-amber-700">2026 avg (first {paired.length} draws):</b> {avg2026}</span>
                            <span className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200">2026 higher: <b className="text-emerald-700">{higher}/{paired.length}</b></span>
                            <span className="px-2 py-1 rounded bg-rose-50 border border-rose-200">2026 lower: <b className="text-rose-700">{lower}/{paired.length}</b></span>
                          </div>
                          <div style={{ width: '100%', height: 380 }} data-testid="paired-graph">
                            <ResponsiveContainer>
                              <BarChart data={paired} margin={{ top: 16, right: 24, left: 8, bottom: 24 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="idx" tick={{ fontSize: 11, fill: '#475569' }} label={{ value: 'Draw # of year (Nth draw)', position: 'insideBottom', offset: -10, fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11, fill: '#475569' }} domain={[0, 240]} />
                                <Tooltip
                                  contentStyle={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                                  labelFormatter={(label, payload) => {
                                    const p = payload && payload[0] ? payload[0].payload : null;
                                    return p ? `Draw #${label} · 2025: ${p.date_2025} | 2026: ${p.date_2026}` : `Draw #${label}`;
                                  }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <ReferenceLine y={data.avg_sum} stroke="#dc2626" strokeDasharray="5 4" label={{ value: `Lifetime avg ${data.avg_sum}`, fill: '#dc2626', fontSize: 10, position: 'right' }} />
                                <Bar dataKey="2025" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="2026" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="mt-2 text-[10px] text-gray-500 italic">
                            <b>Reading:</b> X-axis = Nth draw of the year (1 = first draw of 2025/2026, 2 = second, …). Hover any pair to see the actual dates. <span className="text-blue-700 font-bold">Blue</span> = 2025 sum, <span className="text-amber-700 font-bold">Amber</span> = 2026 sum. The summary chips above tell you whether 2026 is running hotter (more {higher > lower ? 'highs' : 'lows'}) than 2025 at this point.
                          </div>

                          {/* Verdict — % diff bucket distribution + next-draw projection */}
                          {(() => {
                            const valid = paired.filter(p => p['2025'] !== null);
                            const pctDiffs = valid.map(p => ({
                              ...p,
                              pct: ((p['2026'] - p['2025']) / p['2025']) * 100,
                            }));
                            const buckets = [
                              { label: '≤ ±5%', test: x => Math.abs(x) <= 5, color: 'bg-emerald-500', text: 'text-emerald-700', tone: 'bg-emerald-50 border-emerald-300' },
                              { label: '±5% to ±10%', test: x => Math.abs(x) > 5 && Math.abs(x) <= 10, color: 'bg-teal-500', text: 'text-teal-700', tone: 'bg-teal-50 border-teal-300' },
                              { label: '±10% to ±15%', test: x => Math.abs(x) > 10 && Math.abs(x) <= 15, color: 'bg-amber-500', text: 'text-amber-700', tone: 'bg-amber-50 border-amber-300' },
                              { label: '±15% to ±20%', test: x => Math.abs(x) > 15 && Math.abs(x) <= 20, color: 'bg-orange-500', text: 'text-orange-700', tone: 'bg-orange-50 border-orange-300' },
                              { label: '> ±20%', test: x => Math.abs(x) > 20, color: 'bg-rose-500', text: 'text-rose-700', tone: 'bg-rose-50 border-rose-300' },
                            ];
                            const counts = buckets.map(b => ({
                              ...b,
                              count: pctDiffs.filter(p => b.test(p.pct)).length,
                            }));
                            const total = valid.length;
                            counts.forEach(b => { b.pct = total > 0 ? Math.round((b.count / total) * 1000) / 10 : 0; });

                            // Cumulative for "within X%" reading
                            const within10 = counts[0].count + counts[1].count;
                            const within15 = within10 + counts[2].count;
                            const within20 = within15 + counts[3].count;

                            // Mean & std-dev of pct diff
                            const meanPct = pctDiffs.reduce((s, p) => s + p.pct, 0) / Math.max(1, pctDiffs.length);
                            const stdPct = Math.sqrt(pctDiffs.reduce((s, p) => s + Math.pow(p.pct - meanPct, 2), 0) / Math.max(1, pctDiffs.length));

                            // Next-draw projection: take the 2025 sum at position (paired.length + 1)
                            const nextIdx = paired.length;
                            const next2025 = wave2025[nextIdx]?.sum;
                            const next2025Date = wave2025[nextIdx]?.draw_date;
                            const projCenter = next2025 != null ? Math.round(next2025 * (1 + meanPct / 100)) : null;
                            const projLow = next2025 != null ? Math.round(next2025 * (1 + (meanPct - stdPct) / 100)) : null;
                            const projHigh = next2025 != null ? Math.round(next2025 * (1 + (meanPct + stdPct) / 100)) : null;

                            const histogramData = counts.map(b => ({ label: b.label, pct: b.pct, count: b.count }));

                            return (
                              <div className="mt-5 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-400 rounded-lg overflow-hidden" data-testid="paired-verdict">
                                <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 px-4 py-2.5">
                                  <h4 className="text-white font-black text-sm flex items-center gap-2">
                                    <span className="inline-block px-2 py-0.5 bg-slate-900 text-amber-300 rounded text-[10px] font-black tracking-wider">VERDICT</span>
                                    Date-wise % Diff Distribution + Next-Draw Sum Projection
                                  </h4>
                                </div>

                                <div className="p-4 space-y-4">
                                  {/* Bucket histogram */}
                                  <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-2">Distribution of (2026 − 2025) % difference, n={total} pairs</div>
                                    <div className="space-y-1.5">
                                      {histogramData.map((b, i) => (
                                        <div key={b.label} className="flex items-center gap-2 text-[11px]" data-testid={`pct-bucket-${i}`}>
                                          <span className="w-32 text-right text-slate-300 font-mono">{b.label}</span>
                                          <div className="flex-1 bg-slate-700 rounded h-5 overflow-hidden relative">
                                            <div className={`h-5 ${counts[i].color}`} style={{ width: `${b.pct}%` }} />
                                            <span className="absolute left-2 top-0 leading-5 text-white font-mono font-bold text-[10px]">
                                              {b.count} draws ({b.pct}%)
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Cumulative chips */}
                                  <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-emerald-900/30 border-2 border-emerald-400 rounded-lg p-3 text-center">
                                      <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Within ±10%</div>
                                      <div className="text-3xl font-mono font-black text-emerald-300 mt-1">{Math.round(within10/total*100)}%</div>
                                      <div className="text-[10px] text-emerald-400 mt-0.5">{within10}/{total} pairs</div>
                                    </div>
                                    <div className="bg-amber-900/30 border-2 border-amber-400 rounded-lg p-3 text-center">
                                      <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Within ±15%</div>
                                      <div className="text-3xl font-mono font-black text-amber-300 mt-1">{Math.round(within15/total*100)}%</div>
                                      <div className="text-[10px] text-amber-400 mt-0.5">{within15}/{total} pairs</div>
                                    </div>
                                    <div className="bg-orange-900/30 border-2 border-orange-400 rounded-lg p-3 text-center">
                                      <div className="text-[10px] uppercase tracking-wider text-orange-300 font-bold">Within ±20%</div>
                                      <div className="text-3xl font-mono font-black text-orange-300 mt-1">{Math.round(within20/total*100)}%</div>
                                      <div className="text-[10px] text-orange-400 mt-0.5">{within20}/{total} pairs</div>
                                    </div>
                                  </div>

                                  {/* Bias stats */}
                                  <div className="bg-slate-800 rounded-lg p-3 text-[11px] grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                                    <div>
                                      <div className="text-[9px] uppercase text-slate-400 tracking-wider">Mean % diff</div>
                                      <div className={`text-lg font-mono font-black ${meanPct > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                        {meanPct > 0 ? '+' : ''}{meanPct.toFixed(1)}%
                                      </div>
                                      <div className="text-[9px] text-slate-500">{meanPct >= 0 ? '2026 hotter on avg' : '2026 cooler on avg'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[9px] uppercase text-slate-400 tracking-wider">Std deviation</div>
                                      <div className="text-lg font-mono font-black text-violet-300">±{stdPct.toFixed(1)}%</div>
                                      <div className="text-[9px] text-slate-500">spread of % diffs</div>
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                      <div className="text-[9px] uppercase text-slate-400 tracking-wider">2026 vs 2025</div>
                                      <div className="text-sm font-mono font-bold text-slate-200">
                                        <span className="text-emerald-400">{higher}</span> higher · <span className="text-rose-400">{lower}</span> lower
                                      </div>
                                    </div>
                                  </div>

                                  {/* Next-draw projection */}
                                  {next2025 != null ? (
                                    <div className="bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-400 rounded-lg p-4" data-testid="next-projection">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="inline-block px-2 py-0.5 bg-slate-900 text-amber-300 rounded text-[10px] font-black tracking-wider">PROJECTION</span>
                                        <h4 className="text-amber-900 font-black text-sm">Next 2026 Draw — Expected Sum</h4>
                                      </div>
                                      <p className="text-[11px] text-amber-900 mb-3">
                                        Same-position draw in 2025 ({next2025Date}) had sum <b>{next2025}</b>. Applying the historical 2026 vs 2025 % diff distribution (mean {meanPct > 0 ? '+' : ''}{meanPct.toFixed(1)}%, σ ±{stdPct.toFixed(1)}%):
                                      </p>
                                      <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-white rounded-lg p-3 text-center border-2 border-rose-300">
                                          <div className="text-[10px] uppercase text-rose-600 font-bold tracking-wider">Low band</div>
                                          <div className="text-2xl font-mono font-black text-rose-700 mt-1">{projLow}</div>
                                          <div className="text-[10px] text-rose-500 mt-0.5">−1σ ({(meanPct - stdPct).toFixed(1)}%)</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 text-center border-4 border-amber-500 shadow-lg">
                                          <div className="text-[10px] uppercase text-amber-700 font-bold tracking-wider">★ Most likely</div>
                                          <div className="text-3xl font-mono font-black text-amber-800 mt-1">{projCenter}</div>
                                          <div className="text-[10px] text-amber-600 mt-0.5">center ({meanPct >= 0 ? '+' : ''}{meanPct.toFixed(1)}%)</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 text-center border-2 border-emerald-300">
                                          <div className="text-[10px] uppercase text-emerald-600 font-bold tracking-wider">High band</div>
                                          <div className="text-2xl font-mono font-black text-emerald-700 mt-1">{projHigh}</div>
                                          <div className="text-[10px] text-emerald-500 mt-0.5">+1σ ({(meanPct + stdPct).toFixed(1)}%)</div>
                                        </div>
                                      </div>
                                      <div className="mt-3 text-[10px] text-amber-900 bg-white/60 rounded p-2 border border-amber-300 leading-relaxed">
                                        <b>Interpretation:</b> ~68% chance the next 2026 draw sum lands between <b>{projLow}</b> and <b>{projHigh}</b> (1-σ band). ~95% chance between <b>{Math.round(next2025 * (1 + (meanPct - 2*stdPct) / 100))}</b> and <b>{Math.round(next2025 * (1 + (meanPct + 2*stdPct) / 100))}</b> (2-σ band). Center estimate <b>{projCenter}</b> applies the average 2026-vs-2025 bias to the same-position 2025 draw.
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-slate-800 rounded-lg p-3 text-[11px] text-slate-400 italic text-center">
                                      Next 2026 draw position has no matching 2025 reference — projection unavailable.
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </TabsContent>

              {/* === DISTRIBUTION TAB === */}
              <TabsContent value="droughts">
                <DroughtsTab data={data} />
              </TabsContent>

              {/* ============ TAB: DISTRIBUTION ============ */}
              <TabsContent value="distribution">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-4" data-testid="dist-heading">Block 1 — Sum Distribution</h3>
                    <div className="space-y-1" data-testid="sum-distribution">
                      {data.ranges.map(r => (
                        <div key={r.range} className="flex items-center gap-2">
                          <span className="font-mono text-xs w-16 text-right font-bold">{r.range}</span>
                          <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
                            <div className="h-full bg-[#002FA7] rounded transition-all" style={{ width: `${(r.count / maxCount) * 100}%` }} />
                          </div>
                          <span className="font-mono text-xs w-16">{r.count} ({(r.pct * 100).toFixed(1)}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-4">Block 2 — Yearly Average Sums</h3>
                    <div className="flex flex-wrap gap-3" data-testid="yearly-avg">
                      {Object.entries(data.yearly_avg).map(([yr, avg]) => (
                        <div key={yr} className={`px-3 py-2 rounded border ${Math.abs(avg - data.avg_sum) > data.std_dev ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                          <span className="text-xs text-gray-500">{yr}</span>
                          <p className="font-mono font-bold">{avg}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* === LAST 20 DRAWS TAB === */}

              {/* === REPEATS TAB === */}
              <TabsContent value="repeats">
                <div className="space-y-6">
                  {/* Summary */}
                  {data.repeat_analysis && (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-white border rounded p-4 text-center">
                          <p className="text-[10px] uppercase font-bold text-gray-400">Overall Repeat Rate</p>
                          <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{data.repeat_analysis.repeat_rate_all}%</p>
                          <p className="text-xs text-gray-500">{data.repeat_analysis.total_repeats} / {data.total_draws - 1}</p>
                        </div>
                        <div className="bg-white border rounded p-4 text-center bg-blue-50 border-blue-200">
                          <p className="text-[10px] uppercase font-bold text-blue-600">Till 2025 Rate</p>
                          <p className="text-3xl font-mono font-bold text-blue-700 mt-1">{data.repeat_analysis.repeat_rate_2025}%</p>
                          <p className="text-xs text-gray-500">{data.repeat_analysis.repeats_2025} repeats</p>
                        </div>
                        <div className="bg-white border rounded p-4 text-center bg-amber-50 border-amber-200">
                          <p className="text-[10px] uppercase font-bold text-amber-600">2026 Rate</p>
                          <p className="text-3xl font-mono font-bold text-amber-700 mt-1">{data.repeat_analysis.repeat_rate_2026}%</p>
                          <p className="text-xs text-gray-500">{data.repeat_analysis.repeats_2026} repeats</p>
                        </div>
                        <div className="bg-white border rounded p-4 text-center">
                          <p className="text-[10px] uppercase font-bold text-gray-400">Expected in 2026</p>
                          <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{data.repeat_analysis.expected_repeats_2026}</p>
                          <p className="text-xs text-gray-500">Based on {data.repeat_analysis.repeat_rate_2025}% rate</p>
                        </div>
                        <div className={`bg-white border rounded p-4 text-center ${data.repeat_analysis.draws_since_last_repeat > 10 ? 'bg-red-50 border-red-200' : ''}`}>
                          <p className="text-[10px] uppercase font-bold text-gray-400">Since Last Repeat</p>
                          <p className="text-3xl font-mono font-bold mt-1">{data.repeat_analysis.draws_since_last_repeat} draws</p>
                          <p className="text-xs text-gray-500">Last: {data.repeat_analysis.last_repeat_range}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Repeats by Range */}
                        <div className="bg-white border rounded p-4">
                          <h3 className="font-bold text-gray-900 mb-1" data-testid="repeat-by-range-heading">Block 1 — Repeats by Sum Range</h3>
                          <p className="text-xs text-gray-500 mb-3">Which ranges repeat most often (consecutive same range)?</p>
                          <table className="w-full text-sm" data-testid="repeat-by-range-table">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs uppercase">Range</th>
                                <th className="px-3 py-2 text-center text-xs uppercase">Repeats</th>
                                <th className="px-3 py-2 text-center text-xs uppercase">% of All Repeats</th>
                                <th className="px-3 py-2 text-center text-xs uppercase">Bar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(data.repeat_analysis.by_range || []).map((r, i) => (
                                <tr key={r.range} className={i === 0 ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={i === 0 ? { borderLeft: '3px solid #f59e0b' } : {}}>
                                  <td className="px-3 py-2 border-b font-mono font-bold">{r.range}</td>
                                  <td className="px-3 py-2 border-b font-mono text-center font-bold">{r.count}</td>
                                  <td className="px-3 py-2 border-b font-mono text-center">{r.pct}%</td>
                                  <td className="px-3 py-2 border-b">
                                    <div className="w-full bg-gray-100 rounded h-3">
                                      <div className="h-full bg-[#002FA7] rounded" style={{ width: `${(r.count / (data.repeat_analysis.by_range[0]?.count || 1)) * 100}%` }} />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Recent Repeats */}
                        <div className="bg-white border rounded p-4">
                          <h3 className="font-bold text-gray-900 mb-1" data-testid="recent-repeats-heading">Block 2 — Recent Consecutive Repeats</h3>
                          <p className="text-xs text-gray-500 mb-3">Last 15 instances where the same sum range appeared back-to-back</p>
                          <table className="w-full text-xs" data-testid="recent-repeats-table">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-2 py-2 text-center uppercase">Draw 1</th>
                                <th className="px-2 py-2 text-center uppercase">Sum</th>
                                <th className="px-2 py-2 text-center uppercase">Draw 2</th>
                                <th className="px-2 py-2 text-center uppercase">Sum</th>
                                <th className="px-2 py-2 text-center uppercase">Range</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(data.repeat_analysis.recent_repeats || []).reverse().map((r, i) => {
                                const is2026 = r.draw2_date?.includes('/2026');
                                return (
                                  <tr key={i} className={is2026 ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-2 py-1.5 border-b font-mono text-center">#{r.draw1_num} <span className="text-gray-400">{r.draw1_date}</span></td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{r.draw1_sum}</td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center">#{r.draw2_num} <span className="text-gray-400">{r.draw2_date}</span></td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{r.draw2_sum}</td>
                                    <td className="px-2 py-1.5 border-b font-mono text-center font-bold text-[#002FA7]">{r.range}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Yearly Repeat Stats */}
                      <div className="bg-white border rounded p-4">
                        <h3 className="font-bold text-gray-900 mb-1" data-testid="yearly-repeats-heading">Block 3 — Yearly Repeat Rate</h3>
                        <p className="text-xs text-gray-500 mb-3">How many consecutive same-range repeats occurred each year</p>
                        <div className="flex flex-wrap gap-2" data-testid="yearly-repeats">
                          {(data.repeat_analysis.yearly || []).map(yr => {
                            const isHot = yr.pct > data.repeat_analysis.repeat_rate_all + 3;
                            const isCold = yr.pct < data.repeat_analysis.repeat_rate_all - 3;
                            return (
                              <div key={yr.year} className={`px-3 py-2 rounded border text-center min-w-[70px] ${isHot ? 'border-red-300 bg-red-50' : isCold ? 'border-blue-300 bg-blue-50' : 'border-gray-200'} ${yr.year >= 2026 ? 'ring-2 ring-amber-300' : ''}`}>
                                <span className="text-[10px] text-gray-500 block">{yr.year}</span>
                                <span className="font-mono font-bold block">{yr.repeats}</span>
                                <span className={`text-[10px] font-bold ${isHot ? 'text-red-600' : isCold ? 'text-blue-600' : 'text-gray-500'}`}>{yr.pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="recent">
                <div className="bg-white border rounded p-4">
                  <h3 className="font-bold text-gray-900 mb-4" data-testid="recent-heading">Block 1 — All 2026 Draws ({data.sums_2026_wave?.length || 0})</h3>
                  <div className="overflow-x-auto" data-testid="last-20-table">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-center text-xs uppercase">Draw #</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">Date</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">Sum</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">Range</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">vs Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.sums_2026_wave || []).slice().reverse().map((d, i) => {
                          const diff = d.sum - data.avg_sum;
                          return (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2 border-b font-mono text-center">#{d.draw_number}</td>
                              <td className="px-3 py-2 border-b text-center">{d.draw_date}</td>
                              <td className="px-3 py-2 border-b font-mono font-bold text-center text-lg">{d.sum}</td>
                              <td className="px-3 py-2 border-b font-mono font-bold text-center text-[#002FA7]">{d.range}</td>
                              <td className={`px-3 py-2 border-b font-mono text-center font-bold ${diff > 0 ? 'text-green-700' : 'text-red-600'}`}>{diff > 0 ? '+' : ''}{diff.toFixed(1)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        ) : null}
      </main>
    </div>
  );
}


// =====================================================================================
// Droughts Tab — Block 1: Reversion Drought (sum ≥ avg → next sum lower)
//                Block 2: Per-Range Drought (draws since last hit vs max/min gap)
// =====================================================================================
function ReversionStatCards({ stats, label }) {
  if (!stats) return null;
  const cards = [
    { k: 'Current Drought', v: stats.current_drought, sub: 'draws since last event', hi: stats.avg_gap != null && stats.current_drought > stats.avg_gap },
    { k: 'Max Gap', v: stats.max_gap ?? '—', sub: 'longest wait between events' },
    { k: 'Min Gap', v: stats.min_gap ?? '—', sub: 'shortest wait between events' },
    { k: 'Avg Gap', v: stats.avg_gap ?? '—', sub: 'typical wait' },
    { k: 'Total Events', v: stats.events, sub: `${stats.triggers} triggers (sum ≥ avg)` },
    { k: 'Trigger Success', v: `${stats.trigger_success_pct}%`, sub: 'sum ≥ avg → next sum lower', hi: stats.trigger_success_pct >= 60 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`reversion-cards-${label}`}>
      {cards.map((c) => (
        <div key={c.k} className={`rounded-lg border-2 p-3 text-center ${c.hi ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-white'}`}>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{c.k}</div>
          <div className={`text-2xl font-black font-mono ${c.hi ? 'text-rose-700' : 'text-gray-900'}`}>{c.v}</div>
          <div className="text-[9px] text-gray-400">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function DroughtsTab({ data }) {
  const rd = data?.reversion_drought;
  const prd = data?.per_range_drought || [];
  const statusStyle = {
    RECORD: 'bg-red-100 text-red-800 border-red-300',
    OVERDUE: 'bg-amber-100 text-amber-800 border-amber-300',
    OK: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return (
    <div className="space-y-6" data-testid="droughts-tab">
      <div className="bg-gradient-to-r from-rose-600 to-red-600 rounded-xl p-5 text-white shadow-lg">
        <h2 className="text-xl font-black mb-1">Drought Tracker — Reversion &amp; Per-Range Gaps</h2>
        <p className="text-[11px] text-rose-100">
          A <b>reversion event</b> = sum lands at/above the historical average ({rd?.avg_reference}) and the <b>next draw&apos;s sum is lower</b>.
          Drought = number of draws elapsed since the last event, compared against the max/min historical gap between events.
        </p>
      </div>

      {/* Block 1 — Reversion Drought */}
      {rd && (
        <div className="bg-white border rounded p-4" data-testid="reversion-drought-block">
          <h3 className="font-bold text-gray-900 mb-1">Block 1 — Reversion Drought (Sum ≥ Avg → Next Sum Lower)</h3>
          <p className="text-xs text-gray-500 mb-4">
            Uses the dashed average line from the 2026 Sum Trajectory graph (avg = {rd.avg_reference}). All-time uses every draw; the 2026 panel uses only 2026 draws — where you spotted this behaviour.
          </p>

          <div className="mb-5" data-testid="reversion-2026">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-rose-600 text-white px-2 py-0.5 rounded text-[10px] font-black tracking-wider">2026 ONLY</span>
              <span className="text-xs text-gray-500">{rd.y2026?.triggers || 0} triggers this year</span>
            </div>
            <ReversionStatCards stats={rd.y2026} label="2026" />
          </div>

          <div className="mb-5" data-testid="reversion-alltime">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-slate-700 text-white px-2 py-0.5 rounded text-[10px] font-black tracking-wider">ALL-TIME</span>
              <span className="text-xs text-gray-500">full history baseline</span>
            </div>
            <ReversionStatCards stats={rd.all_time} label="alltime" />
          </div>

          {(rd.all_time?.recent_events || []).length > 0 && (
            <div>
              <h4 className="font-bold text-sm text-gray-800 mb-2">Last {rd.all_time.recent_events.length} Reversion Events (most recent first)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reversion-events-table">
                  <thead className="bg-rose-50">
                    <tr>
                      <th className="px-3 py-2 text-center text-xs uppercase">Trigger Date</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Trigger Sum (≥ avg)</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Next Date</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Next Sum</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Drop</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rd.all_time.recent_events.map((e) => (
                      <tr key={`${e.trigger_date}-${e.next_date}`} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 border-b text-center">{e.trigger_date}</td>
                        <td className="px-3 py-2 border-b text-center font-mono font-bold text-emerald-700">{e.trigger_sum}</td>
                        <td className="px-3 py-2 border-b text-center">{e.next_date}</td>
                        <td className="px-3 py-2 border-b text-center font-mono font-bold text-rose-700">{e.next_sum}</td>
                        <td className="px-3 py-2 border-b text-center font-mono font-bold text-red-600">−{e.drop}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Block 2 — Per-Range Drought */}
      <div className="bg-white border rounded p-4" data-testid="per-range-drought-block">
        <h3 className="font-bold text-gray-900 mb-1">Block 2 — Per-Range Drought (Draws Since Last Hit vs Historical Gaps)</h3>
        <p className="text-xs text-gray-500 mb-3">
          For each sum range: how many draws since it last hit (1 = hit in the most recent draw), compared to its min / avg / max historical gap.
          <span className="font-bold text-red-700"> RECORD</span> = current drought ≥ all-time max gap. <span className="font-bold text-amber-700">OVERDUE</span> = current drought above average gap.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-range-drought-table">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-3 py-2 text-center text-xs uppercase">Range</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Hits</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Current Drought</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Min Gap</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Avg Gap</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Max Gap</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Expected Gap</th>
                <th className="px-3 py-2 text-center text-xs uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {prd.map((r) => (
                <tr key={r.range} data-testid={`drought-row-${r.range}`} className={r.status === 'RECORD' ? 'bg-red-50' : r.status === 'OVERDUE' ? 'bg-amber-50' : 'odd:bg-white even:bg-gray-50'}>
                  <td className="px-3 py-2 border-b text-center font-mono font-bold text-[#002FA7]">{r.range}</td>
                  <td className="px-3 py-2 border-b text-center font-mono">{r.count}</td>
                  <td className="px-3 py-2 border-b text-center font-mono font-black text-lg">{r.current_gap}</td>
                  <td className="px-3 py-2 border-b text-center font-mono">{r.min_gap ?? '—'}</td>
                  <td className="px-3 py-2 border-b text-center font-mono">{r.avg_gap ?? '—'}</td>
                  <td className="px-3 py-2 border-b text-center font-mono">{r.max_gap ?? '—'}</td>
                  <td className="px-3 py-2 border-b text-center font-mono text-gray-500">{r.expected_gap}</td>
                  <td className="px-3 py-2 border-b text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${statusStyle[r.status]}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// =====================================================================================
// Formula 2 Tab — Next-draw additive scoring for sum ranges
// Components: Trans + Gap + Year + Base + DoW
// =====================================================================================
function Formula2RangesTab({ data }) {
  const ranges = [...(data?.range_predictions || [])].sort((a, b) => (b.combined_score_v2 || 0) - (a.combined_score_v2 || 0));
  if (ranges.length === 0) return <div className="bg-white border rounded p-6 text-sm text-gray-500">No data.</div>;

  const top3Threshold = ranges.length >= 3 ? ranges[2].combined_score_v2 : 0;
  const lastRange = data.convergence?.last_range || '';
  const dowMap = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const nextDow = data.convergence?.next_dow != null ? dowMap[data.convergence.next_dow] : '?';

  return (
    <div className="space-y-5" data-testid="formula2-ranges-tab">
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-xl p-5 text-white shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-black tracking-wider">FORMULA 2</span>
          <h2 className="text-xl font-black">Next-Draw Predictor (4-component additive)</h2>
        </div>
        <p className="text-[11px] text-orange-100 font-mono">
          Last range: <span className="font-bold text-white">{lastRange}</span>  ·  Next draw DoW: <span className="font-bold text-white">{nextDow}</span>  ·  Score = T (trans) + G (gap) + Y (year) + B (base) + DoW
        </p>
      </div>

      <div className="bg-white border-2 border-orange-200 rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-orange-50 border-b-2 border-orange-200">
              <tr className="text-[10px] uppercase tracking-wider text-orange-900">
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Range</th>
                <th className="px-3 py-2 text-center">T (trans %)</th>
                <th className="px-3 py-2 text-center">G (gap)</th>
                <th className="px-3 py-2 text-center">Y (year)</th>
                <th className="px-3 py-2 text-center">B (base)</th>
                <th className="px-3 py-2 text-center">DoW</th>
                <th className="px-3 py-2 text-center">M2</th>
                <th className="px-3 py-2 text-center bg-orange-100 font-black">Score</th>
                <th className="px-3 py-2 text-center text-[10px]">2026</th>
              </tr>
            </thead>
            <tbody>
              {ranges.map((r, i) => {
                const isTop3 = (r.combined_score_v2 || 0) >= top3Threshold && top3Threshold > 0;
                return (
                  <tr key={r.range} className={`border-b border-orange-100 ${isTop3 ? 'bg-orange-50' : 'hover:bg-gray-50'}`} data-testid={`f2-row-${r.range}`}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">#{i + 1}</td>
                    <td className="px-3 py-2 font-mono font-bold text-gray-900">{r.range}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <div className="font-bold text-orange-700">{r.trans_score}</div>
                      <div className="text-[9px] text-gray-400">({r.trans_pct}%)</div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <div className="font-bold text-orange-700">{r.gap_score_v2}</div>
                      <div className="text-[9px] text-gray-400">({r.overdue_ratio}x)</div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <div className={`font-bold ${(r.year_score || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.year_score}</div>
                      <div className="text-[9px] text-gray-400">({r.year_diff > 0 ? '+' : ''}{r.year_diff}%)</div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <div className="font-bold text-orange-700">{r.base_score}</div>
                      <div className="text-[9px] text-gray-400">({r.freq_pct}%)</div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <div className={`font-bold ${(r.dow_bonus || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.dow_bonus}</div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <div className={`font-bold ${(r.markov2_score || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{r.markov2_score || 0}</div>
                      <div className="text-[9px] text-gray-400">({r.markov2_pct || 0}%)</div>
                    </td>
                    <td className="px-3 py-2 text-center" data-testid={`f2-score-${r.range}`}>
                      {isTop3 ? (
                        <span className="inline-flex w-12 h-12 rounded-full items-center justify-center bg-orange-500 text-white font-mono font-black text-sm shadow-md ring-2 ring-orange-200">
                          {r.combined_score_v2}
                        </span>
                      ) : (
                        <span className="font-mono font-bold text-orange-800">{r.combined_score_v2}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-[10px]">
                      {r.count_2026 > 0 ? (
                        <span className="font-bold text-emerald-700">{r.count_2026}x ({r.pct_2026}%)</span>
                      ) : (
                        <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">MISSING</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
 * W2 MASTER VERDICT CARD — direction-anchored consolidated call across every
 * W2 sub-window signal. Placed at the very top of the Consolidated tab so
 * it is the first thing the user sees when opening W2.
 *
 * Logic:
 *   1. The Direction Call is the strongest backtested W2 signal (~69% historical).
 *      Treat it as the anchor.
 *   2. Every sub-window range projection (from `range_predictions` sorted by
 *      combined_score_v2) is compared to the direction: does its centre fall
 *      on the same side of prev_sum as the direction call?
 *   3. Ranges that ALIGN with the direction are kept; ranges that CONTRADICT
 *      are shown with a red warning but excluded from the consolidated pool
 *      (per user rule: contradictions surfaced, but Direction wins).
 *   4. The top-3 aligned ranges form the "consolidated" corridor.
 *   5. Per-ball top-3 numbers are fetched from the W2 difference-matrix
 *      endpoint and shown as the actionable pick.
 *   6. A single one-line recommendation combines direction + corridor +
 *      exact best + top-3 numbers per ball.
 * ========================================================================== */
function W2MasterVerdictCard({ dc, rangePredictions = [], avgSum }) {
  const [directionAcc, setDirectionAcc] = useState(null);
  const [perBall, setPerBall] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Direction Call historical accuracy from the accuracy tracker.
        const r1 = await axios.get(`${API}/analysis/window-accuracy?test_count=50`);
        const dcAcc = r1.data?.w2_direction_corridor_accuracy;
        if (!cancelled && dcAcc) {
          setDirectionAcc({
            direction: dcAcc.direction?.pct,
            corridor:  dcAcc.primary_corridor?.pct,
            pool:      dcAcc.extended_pool?.pct,
            exact:     dcAcc.exact_range?.pct,
            tested:    dcAcc.tested,
          });
        }
        // Per-ball top-3 from W2 difference-matrix (used for the actionable
        // number pick at the end of the master card).
        const r2 = await axios.get(`${API}/analysis/difference-matrix`);
        const pbp = r2.data?.per_ball_predictions || {};
        if (!cancelled) {
          const map = {};
          ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'].forEach((bk) => {
            map[bk] = (pbp[bk] || []).slice(0, 3).map((p) => ({
              number: p.number,
              score: Math.round((p.pred_score || 0) * 100) / 100,
            }));
          });
          setPerBall(map);
        }
      } catch (_e) { /* silent — master card degrades gracefully */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!dc) {
    return (
      <div className="rounded-xl p-5 bg-slate-100 border-2 border-slate-300 text-slate-600 text-center" data-testid="w2-master-verdict-empty">
        Master verdict unavailable — need at least 30 draws.
      </div>
    );
  }

  const lower = dc.direction === 'LOWER';
  const prevSum = dc.prev_sum;

  // Rank + classify sub-window range projections against the Direction Call.
  const rangeCenter = (label) => {
    const parts = (label || '').split('-');
    if (parts.length !== 2) return null;
    const [lo, hi] = parts.map((x) => parseInt(x, 10));
    if (Number.isNaN(lo) || Number.isNaN(hi)) return null;
    return (lo + hi) / 2;
  };
  const sortedRanges = [...(rangePredictions || [])]
    .filter((r) => rangeCenter(r.range) != null)
    .sort((a, b) => (b.combined_score_v2 || 0) - (a.combined_score_v2 || 0))
    .slice(0, 8);
  const classified = sortedRanges.map((r) => {
    const c = rangeCenter(r.range);
    const aligns = lower ? (c < prevSum) : (c > prevSum);
    return {
      range: r.range,
      score: r.combined_score_v2 || 0,
      pred_score: r.pred_score || 0,
      overdue: r.overdue_ratio || 0,
      aligns,
    };
  });
  const alignedRanges = classified.filter((r) => r.aligns);
  const contradicting = classified.filter((r) => !r.aligns);
  const consolidatedCorridor = alignedRanges.slice(0, 3).map((r) => r.range);

  const dirBadge = lower ? 'bg-rose-600' : 'bg-emerald-600';
  const dirArrow = lower ? '⬇' : '⬆';

  // Assemble a single-line final recommendation.
  const topNums = perBall ? ['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk) => (perBall[bk]?.[0]?.number ?? '?')) : null;
  const oneLine = topNums
    ? `${dirArrow} ${dc.direction}  ·  corridor ${dc.corridor?.label}  ·  exact best ${dc.exact_best}  ·  bet numbers B1=${topNums[0]}, B2=${topNums[1]}, B3=${topNums[2]}, B4=${topNums[3]}, B5=${topNums[4]}`
    : `${dirArrow} ${dc.direction}  ·  corridor ${dc.corridor?.label}  ·  exact best ${dc.exact_best}`;

  return (
    <div className={`rounded-xl overflow-hidden border-4 ${lower ? 'border-rose-500' : 'border-emerald-500'} shadow-2xl`} data-testid="w2-master-verdict-card">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 px-5 py-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-xl font-black text-white uppercase tracking-wider">
              🎯 W2 Master Verdict
            </h3>
            <p className="text-xs text-white/70 mt-0.5">
              Direction-anchored consolidated call across every W2 sub-window signal.
              Direction is the strongest historical signal ({directionAcc?.direction != null ? `${directionAcc.direction}% backtested over last ${directionAcc.tested} draws` : 'backtest loading…'}), so it wins any contradiction.
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-white font-black uppercase tracking-widest text-sm ${dirBadge}`}>
            Anchor: {dirArrow} {dc.direction}
          </div>
        </div>
      </div>

      {/* Primary Direction Call */}
      <div className={`px-5 py-4 ${lower ? 'bg-gradient-to-br from-rose-50 via-pink-50 to-rose-100' : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100'}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Direction</p>
            <p className={`text-3xl font-black leading-none ${lower ? 'text-rose-700' : 'text-emerald-700'}`}>
              {dirArrow} {dc.direction}
            </p>
            <p className="text-[10px] text-slate-500 mt-1 font-mono">
              live conf {dc.direction_pct}% · backtest {directionAcc?.direction ?? '—'}%
            </p>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Prev Sum</p>
            <p className="text-3xl font-black leading-none text-slate-800 font-mono">{prevSum}</p>
            <p className="text-[10px] text-slate-500 mt-1">avg {avgSum}</p>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Corridor</p>
            <p className="text-2xl font-black leading-none text-slate-800 font-mono">{dc.corridor?.label}</p>
            <p className="text-[10px] text-slate-500 mt-1 font-mono">
              backtest {directionAcc?.corridor ?? '—'}%
            </p>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Exact Best</p>
            <p className="text-2xl font-black leading-none text-slate-800 font-mono">{dc.exact_best}</p>
            <p className="text-[10px] text-slate-500 mt-1 font-mono">
              backtest {directionAcc?.exact ?? '—'}%
            </p>
          </div>
        </div>
      </div>

      {/* Sub-window alignment check */}
      <div className="px-5 py-4 bg-white border-t border-slate-200">
        <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-2 flex items-center gap-2">
          Sub-window alignment check
          {contradicting.length > 0 && (
            <span className="px-2 py-0.5 rounded bg-rose-600 text-white text-[9px] font-black uppercase animate-pulse">
              ⚠ {contradicting.length} contradiction{contradicting.length > 1 ? 's' : ''}
            </span>
          )}
        </h4>
        <p className="text-[11px] text-slate-500 mb-3">
          Top W2 range projections ranked by combined_score_v2, each classified against the Direction Call.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 text-slate-700 uppercase text-[10px] font-black">
                <th className="text-left px-2 py-1.5">Range</th>
                <th className="text-right px-2 py-1.5">Centre</th>
                <th className="text-right px-2 py-1.5">Combined score</th>
                <th className="text-right px-2 py-1.5">Overdue×</th>
                <th className="text-center px-2 py-1.5">Alignment</th>
              </tr>
            </thead>
            <tbody>
              {classified.map((r) => (
                <tr key={r.range} className={`border-t border-slate-100 ${r.aligns ? 'bg-emerald-50/40' : 'bg-rose-50/40'}`} data-testid={`w2-master-align-row-${r.range}`}>
                  <td className="px-2 py-1.5 font-mono font-bold text-slate-800">{r.range}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-600">{rangeCenter(r.range)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-800">{r.score.toFixed(1)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-600">{r.overdue?.toFixed(1) ?? '—'}</td>
                  <td className="px-2 py-1.5 text-center">
                    {r.aligns ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-black uppercase">✓ Aligns</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded bg-rose-600 text-white text-[9px] font-black uppercase" title="Contradicts the Direction Call — overridden">
                        ✕ Contradicts
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {classified.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 italic py-3">No sub-window range projections available.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Consolidated pool (aligned ranges only) + Per-ball top-3 */}
      <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Consolidated corridor (aligned only)</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {consolidatedCorridor.length === 0 && <span className="text-slate-400 italic text-xs">—</span>}
              {consolidatedCorridor.map((r, i) => (
                <span key={r}
                      className={`inline-block px-2.5 py-1 rounded font-mono font-black text-sm ${i === 0 ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-900'}`}
                      data-testid={`w2-master-consol-${r}`}>
                  {i === 0 ? '★ ' : ''}{r}
                </span>
              ))}
            </div>
            {contradicting.length > 0 && (
              <p className="text-[10px] text-rose-700 mt-2 italic">
                Excluded from pool: {contradicting.map((c) => c.range).join(', ')} — contradict {dc.direction}.
              </p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Top-3 per ball (W2 pred_score)</p>
            <div className="grid grid-cols-5 gap-2 mt-1">
              {['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, i) => (
                <div key={bk} className="rounded border border-slate-200 bg-white p-1.5 text-center" data-testid={`w2-master-perball-${bk}`}>
                  <p className="text-[9px] text-slate-500 font-black uppercase">B{i + 1}</p>
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {(perBall?.[bk] || []).map((n, ni) => (
                      <span key={n.number}
                            className={`text-[11px] font-mono font-black rounded px-1 py-0.5 ${ni === 0 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-800'}`}
                            title={`pred_score ${n.score}`}>
                        {n.number}
                      </span>
                    ))}
                    {!perBall && <span className="text-[10px] text-slate-400">…</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Final one-line recommendation */}
      <div className={`px-5 py-4 ${lower ? 'bg-gradient-to-r from-rose-700 via-rose-600 to-rose-700' : 'bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700'}`}>
        <p className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-1">
          🎯 Final recommendation
        </p>
        <p className="text-white text-sm md:text-base font-black tracking-wide leading-snug font-mono" data-testid="w2-master-final-line">
          {oneLine}
        </p>
      </div>
    </div>
  );
}


/* ==========================================================================
 * DIRECTION + CORRIDOR CARD — the two strongest backtested W2 signals:
 *   Direction (mean-reversion vs avg, ~69-73% historical)
 *   Primary corridor (Borda top-1 ±1 range, ≈41% backtested)
 *   Extended pool (corridor ∪ reversion-target band, ≈53% backtested)
 * ========================================================================== */
function DirectionCorridorCard({ dc }) {
  if (!dc) return null;
  const lower = dc.direction === 'LOWER';
  return (
    <div className="rounded-xl overflow-hidden shadow-lg border-2 border-slate-700" data-testid="w2-direction-corridor-card">
      <div className="bg-slate-900 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-black tracking-widest text-teal-400 uppercase">Direction + Corridor Verdict</div>
          <div className="text-xs text-slate-400">Built from the two strongest backtested W2 signals · live accuracy tracked in W50</div>
        </div>
        <div className="text-[10px] text-slate-400 font-mono">Last sum <span className="text-white font-bold">{dc.prev_sum}</span> vs avg <span className="text-white font-bold">{dc.avg}</span></div>
      </div>
      <div className="bg-slate-800 p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Direction */}
        <div className={`rounded-lg p-4 border-2 ${lower ? 'border-rose-500/60 bg-rose-950/40' : 'border-emerald-500/60 bg-emerald-950/40'}`} data-testid="dc-direction">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Next Sum Direction</div>
          <div className={`text-3xl font-black flex items-center gap-2 ${lower ? 'text-rose-400' : 'text-emerald-400'}`}>
            {lower ? '⬇ LOWER' : '⬆ HIGHER'}
          </div>
          <div className="text-xs text-slate-300 mt-1">
            <span className="font-mono font-bold text-white">{dc.direction_pct}%</span> historical — sum {lower ? '≥' : '<'} avg → next sum {lower ? 'lower' : 'higher'}
          </div>
        </div>
        {/* Primary corridor */}
        <div className="rounded-lg p-4 border-2 border-teal-500/60 bg-teal-950/40" data-testid="dc-corridor">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Primary Corridor (30 pts)</div>
          <div className="text-3xl font-black text-teal-300 font-mono">{dc.corridor?.label}</div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {(dc.corridor?.ranges || []).map(r => (
              <span key={r} className={`px-2 py-0.5 rounded-full text-[10px] font-bold font-mono ${r === dc.exact_best ? 'bg-amber-400 text-amber-950' : 'bg-teal-700/60 text-teal-100'}`}>{r}</span>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-1.5">≈41% backtested · <span className="text-amber-300 font-bold">gold = exact best guess</span></div>
        </div>
        {/* Extended pool */}
        <div className="rounded-lg p-4 border-2 border-indigo-500/60 bg-indigo-950/40" data-testid="dc-pool">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Extended Pool ({(dc.extended_pool || []).length} ranges)</div>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            {(dc.extended_pool || []).map(r => (
              <span key={r} className="px-2 py-1 rounded-md text-xs font-bold font-mono bg-indigo-700/60 text-indigo-100">{r}</span>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-2">Corridor ∪ reversion-target band · ≈53% backtested</div>
        </div>
      </div>
    </div>
  );
}


/* ==========================================================================
 * W2 CONSOLIDATED VERDICT
 * Aggregates verdicts across every sub-window of W2 (Sum Range):
 *   1) My Sum Range Prediction (user-settable constraint)
 *   2) Master Verdict — cross-section signal tally per range
 *   3) Signal Matrix — which section picked which range (visual grid)
 *   4) Bottom line — top-3 consensus + confidence tier
 * ========================================================================== */
function W2ConsolidatedVerdict({
  data,
  sumSet,
  handleSumSetChange,
  dbSets,
  applied,
  sumMin,
  setSumMin,
  sumMax,
  setSumMax,
  setApplied,
  saveSumRange,
  clearConstraint,
  selectedCoverage,
  isInRange,
}) {
  // --- Extract each sub-window's top-5 ranges ---
  const convElite = (data.convergence?.elite || []);
  const convStrong = (data.convergence?.strong || []);
  const transAfter2 = (data.transition?.after_2nd_order || []).slice(0, 3).map(t => t.range);
  const lastPair = data.transition?.last_pair || '';
  const repeatRanges = (data.repeat_analysis?.by_range || []).slice(0, 3).map(r => r.range);

  // --- Line-graph (2026 Sum Trajectory) streak analysis ---
  // Pull the backend-computed streak history (true 22-year baseline, not 2026-only)
  const streakHist = data.streak_history || null;
  const lineStreak = (() => {
    if (!streakHist || !streakHist.current || !streakHist.current.side) return null;
    const cur = streakHist.current;
    const maxA = streakHist.all_time_max?.above || 0;
    const maxB = streakHist.all_time_max?.below || 0;
    // Next-draw hint: streak >= all-time max → reversion; else continue
    const limit = cur.side === "above" ? maxA : maxB;
    const nextSideHint = cur.len >= limit
      ? (cur.side === "above" ? "below" : "above")
      : cur.side;
    return {
      avg: streakHist.avg_reference,
      curSide: cur.side,
      curLen: cur.len,
      curEndSum: cur.end_sum,
      curEndDate: cur.end_date,
      maxAbove: maxA,
      maxBelow: maxB,
      baselines: streakHist.baselines || {},
      currentYear: streakHist.current_year || {},
      nextSideHint,
    };
  })();

  // Map the line-graph hint onto ranges: above = ≥150, below = <100, else neutral
  const lineGraphTopRanges = (() => {
    if (!lineStreak) return [];
    if (lineStreak.nextSideHint === 'above') return ['150-159', '160-169', '170-179', '180-189'];
    if (lineStreak.nextSideHint === 'below') return ['80-89', '90-99', '100-109'];
    return [];
  })();

  // --- Build the signal matrix: range -> { source -> rank/hit } ---
  const ALL_RANGES = (data.ranges || []).map(r => r.range);
  const rank = (arr, r) => { const i = arr.indexOf(r); return i < 0 ? null : i + 1; };
  const contains = (arr, r) => arr.includes(r);

  const matrix = ALL_RANGES.map(r => {
    const sources = [
      { key: 'elite', label: 'Convergence ELITE', rank: contains(convElite, r) ? 1 : null },
      { key: 'strong', label: 'Convergence STRONG', rank: contains(convStrong, r) ? 1 : null },
      { key: 'trans_after2', label: `After ${lastPair}`, rank: rank(transAfter2, r) },
      { key: 'repeats', label: 'Repeat Regime', rank: rank(repeatRanges, r) },
      { key: 'line', label: 'Line-Graph Trend', rank: contains(lineGraphTopRanges, r) ? 1 : null },
    ];
    const hits = sources.filter(s => s.rank !== null).length;
    const avgRank = hits > 0
      ? sources.filter(s => s.rank !== null).reduce((a, s) => a + s.rank, 0) / hits
      : null;
    const score = hits * 10 + (avgRank != null ? (6 - Math.min(avgRank, 5)) : 0);
    return { range: r, sources, hits, avgRank, score };
  }).sort((a, b) => b.score - a.score);

  const topThree = matrix.slice(0, 3);
  const maxHits = matrix[0]?.hits || 0;
  const tier = maxHits >= 5 ? 'ELITE' : maxHits >= 3 ? 'STRONG' : maxHits >= 2 ? 'MODERATE' : 'WEAK';
  const tierColor = tier === 'ELITE' ? 'from-emerald-600 to-teal-700'
    : tier === 'STRONG' ? 'from-blue-600 to-indigo-700'
    : tier === 'MODERATE' ? 'from-amber-500 to-orange-600'
    : 'from-slate-500 to-slate-700';

  return (
    <div className="space-y-5" data-testid="w2-consolidated-verdict">
      {/* === 🎯 MASTER VERDICT — direction-anchored, sub-window contradiction-aware === */}
      <W2MasterVerdictCard
        dc={data.direction_corridor}
        rangePredictions={data.range_predictions}
        avgSum={data.avg_sum}
      />

      {/* === DIRECTION + CORRIDOR — strongest backtested signals (69% dir / 41% corridor / 53% pool) === */}
      <DirectionCorridorCard dc={data.direction_corridor} />

      {/* === FINAL VERDICT — unified across Master Voting + Streak Path Probability + user constraint === */}
      <FinalVerdictBlock
        masterMatrix={matrix}
        streakDeep={data.streak_deep_analysis}
        avgSum={data.avg_sum}
        sumMin={sumMin}
        sumMax={sumMax}
        applied={applied}
      />

      {/* === BLOCK 1 — My Sum Range Prediction (user constraint) === */}
      <div className={`rounded-lg p-5 text-white transition-all ${applied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid="my-prediction-section-consolidated">
        <div className="flex items-center gap-1.5 mb-3">
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => handleSumSetChange(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${sumSet === s ? (s===1?'bg-blue-600':s===2?'bg-purple-600':'bg-teal-600')+' text-white ring-2 ring-white/40 shadow-lg' : dbSets[`C${s}`]?.sum_range_min != null ? 'bg-white/20 text-white/90' : 'bg-white/10 text-white/50 hover:bg-white/15'}`} data-testid={`consol-sum-set-${s}`}>C{s}</button>
          ))}
          <span className="text-[10px] text-white/40 ml-2">3 independent sets</span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-lg">Block 1 — My Sum Range Prediction</h3>
            <p className="text-xs text-white/70">Set your predicted sum range — this constraint applies across all prediction windows</p>
          </div>
          {applied ? (
            <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300" data-testid="consol-constraint-status">
              <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
            </span>
          ) : (
            <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60" data-testid="consol-constraint-status">NOT SET</span>
          )}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold opacity-80">Min Sum:</label>
            <input type="number" value={sumMin} onChange={e => { setSumMin(parseInt(e.target.value) || 0); setApplied(false); }} className="w-20 px-2 py-1.5 rounded text-gray-900 font-mono font-bold text-center" min={40} max={250} data-testid="consol-sum-min-input" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold opacity-80">Max Sum:</label>
            <input type="number" value={sumMax} onChange={e => { setSumMax(parseInt(e.target.value) || 0); setApplied(false); }} className="w-20 px-2 py-1.5 rounded text-gray-900 font-mono font-bold text-center" min={40} max={250} data-testid="consol-sum-max-input" />
          </div>
          <Button onClick={saveSumRange} className={`font-bold transition-all ${applied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid="consol-save-sum-range-btn">{applied ? 'Applied' : 'Save Constraint'}</Button>
          {applied && <Button onClick={clearConstraint} variant="ghost" className="text-white/70 hover:text-white text-xs" data-testid="consol-clear-constraint-btn">Clear</Button>}
        </div>
        {selectedCoverage && (
          <div className="flex items-center gap-6 mt-3 flex-wrap">
            <div className="bg-white/15 rounded px-3 py-2"><span className="text-[10px] uppercase opacity-70 block">Overall Coverage</span><span className="font-mono font-bold text-lg">{selectedCoverage.pctAll}%</span><span className="text-xs opacity-70 ml-1">({selectedCoverage.countAll}/{data.total_draws})</span></div>
            <div className="bg-white/15 rounded px-3 py-2"><span className="text-[10px] uppercase opacity-70 block">2026 Coverage</span><span className="font-mono font-bold text-lg">{selectedCoverage.pct26}%</span><span className="text-xs opacity-70 ml-1">({selectedCoverage.count26}/{data.total_2026})</span></div>
            <div className="bg-white/15 rounded px-3 py-2"><span className="text-[10px] uppercase opacity-70 block">Ranges Included</span><span className="font-mono font-bold text-lg">{(data.range_predictions || []).filter(p => isInRange(p.range)).length}</span></div>
            <div className="bg-white/15 rounded px-3 py-2"><span className="text-[10px] uppercase opacity-70 block">Overdue in Zone</span><span className="font-mono font-bold text-lg">{(data.range_predictions || []).filter(p => isInRange(p.range) && p.overdue_ratio >= 1).length}</span></div>
          </div>
        )}
      </div>

      {/* === STREAK ANALYSIS (Line-Graph based, backed by 22-year baseline) === */}
      {lineStreak && <StreakPanel lineStreak={lineStreak} compact={false} />}

      {/* === STREAK DEEP ANALYSIS (dual-formula × 15 states × missing-range verdict) === */}
      {data.streak_deep_analysis && <StreakDeepAnalysis sda={data.streak_deep_analysis} />}

      {/* === BLOCK 2 — Signal Matrix (every range × every sub-window) === */}
      <div className="bg-white border rounded-xl p-5 shadow" data-testid="w2-signal-matrix-block">
        <h3 className="font-bold text-gray-900 mb-1">Block 2 — Full Signal Matrix</h3>
        <p className="text-xs text-gray-500 mb-3">Each row is a sum range; each column is one sub-window. A filled cell shows that sub-window&apos;s rank for that range (lower = higher pick). F1 &amp; F2 are excluded — their combined verdict is in Convergence ELITE/STRONG.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="w2-signal-matrix-table">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left sticky left-0 bg-gray-100 z-10">Range</th>
                <th className="px-2 py-2 text-center text-emerald-700" title="Both F1 and F2 top-3">ELITE</th>
                <th className="px-2 py-2 text-center text-blue-700" title="One of F1/F2 top-3">STRONG</th>
                <th className="px-2 py-2 text-center" title={`What historically follows the pair ${lastPair}`}>After {lastPair}</th>
                <th className="px-2 py-2 text-center" title="Top repeat-regime ranges">Repeats</th>
                <th className="px-2 py-2 text-center" title={`Line-graph streak hint for next draw`}>Line-Graph</th>
                <th className="px-2 py-2 text-center font-bold bg-gray-200">Hits</th>
                <th className="px-2 py-2 text-center font-bold bg-gray-200">Score</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, i) => (
                <tr key={row.range} className={`${i < 3 ? 'bg-emerald-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b`}>
                  <td className="px-2 py-1.5 font-mono font-bold sticky left-0 bg-inherit">{row.range}</td>
                  {row.sources.map(s => (
                    <td key={s.key} className="px-2 py-1.5 text-center">
                      {s.rank !== null ? (
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-mono font-black text-[10px] ${s.rank === 1 ? 'bg-amber-400 text-amber-900' : s.rank <= 3 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>#{s.rank}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center font-mono font-black bg-gray-50">{row.hits}</td>
                  <td className="px-2 py-1.5 text-center font-mono font-black bg-gray-50">{row.score.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-gray-500 mt-3 italic">
          Dig into each tab ({'\u2192'} Convergence, Formula 1, Formula 2, Before &amp; After, etc.) to see the supporting evidence for each cell.
        </p>
      </div>
    </div>
  );
}


/* ==========================================================================
 * FINAL VERDICT BLOCK — Unified prediction across all sub-window signals.
 *
 * Blends THREE evidence sources for each sum-range:
 *   (a) Master Verdict score    (sub-window voting: hits × 10 + rank bonus)
 *   (b) Streak Path Probability (P(break)×P(rng|break) + P(cont)×P(rng|cont))
 *   (c) Range Repetition rate   (small bonus for high self-repeat or "due" gap)
 *
 * Then applies the user's saved Min/Max constraint as a HARD filter.
 * Output is ONE top pick + a 3-row evidence table for transparency.
 * ========================================================================== */
function FinalVerdictBlock({ masterMatrix = [], streakDeep, avgSum, sumMin, sumMax, applied }) {
  const verdict = (() => {
    if (!masterMatrix.length && !streakDeep) return null;

    const rangeMap = {};
    masterMatrix.forEach(row => {
      rangeMap[row.range] = rangeMap[row.range] || { range: row.range, master_score: 0, master_hits: 0, master_avg_rank: null, streak_pct: 0, repetition_self: 0, repetition_gap: null, sources: [] };
      rangeMap[row.range].master_score = row.score;
      rangeMap[row.range].master_hits = row.hits;
      rangeMap[row.range].master_avg_rank = row.avgRank;
      rangeMap[row.range].sources = row.sources.filter(s => s.rank !== null).map(s => s.key);
    });
    if (streakDeep?.verdict?.combined_top3) {
      streakDeep.verdict.combined_top3.forEach(t => {
        rangeMap[t.range] = rangeMap[t.range] || { range: t.range, master_score: 0, master_hits: 0, master_avg_rank: null, streak_pct: 0, repetition_self: 0, repetition_gap: null, sources: [] };
        rangeMap[t.range].streak_pct = t.pct;
      });
    }
    if (streakDeep?.range_repetition) {
      streakDeep.range_repetition.forEach(r => {
        if (rangeMap[r.range]) {
          rangeMap[r.range].repetition_self = r.self_repeat_pct;
          rangeMap[r.range].repetition_gap = r.current_gap;
          rangeMap[r.range].repetition_avg_gap = r.avg_gap;
          rangeMap[r.range].is_missing_in_2026 = r.is_missing_in_2026;
        }
      });
    }

    const inRange = (rangeStr) => {
      if (!applied || sumMin == null || sumMax == null) return true;
      try {
        const [lo, hi] = rangeStr.split('-').map(Number);
        return hi >= sumMin && lo <= sumMax;
      } catch { return true; }
    };

    const candidates = Object.values(rangeMap)
      .filter(c => inRange(c.range))
      .map(c => {
        const masterNorm = Math.min(100, c.master_score * 1.4);
        const streakNorm = Math.min(100, c.streak_pct * 3.5);
        const repNorm = Math.min(100, c.repetition_self * 4);
        const unified = masterNorm * 0.5 + streakNorm * 0.35 + repNorm * 0.15;
        return { ...c, masterNorm, streakNorm, repNorm, unified: Math.round(unified * 10) / 10 };
      })
      .sort((a, b) => b.unified - a.unified);

    if (candidates.length === 0) return null;
    const top = candidates[0];
    const second = candidates[1];
    const margin = second ? Math.round((top.unified - second.unified) * 10) / 10 : top.unified;

    let side = '?';
    try {
      const lo = parseInt(top.range.split('-')[0], 10);
      side = lo < avgSum ? 'below' : 'above';
    } catch { /* ignore */ }

    let confidence = 'LOW';
    if (top.unified >= 60 && margin >= 10) confidence = 'HIGH';
    else if (top.unified >= 40 || margin >= 6) confidence = 'MEDIUM';

    return { top, second, third: candidates[2] || null, margin, side, confidence, totalCandidates: candidates.length, applied: !!applied, sumMin, sumMax };
  })();

  if (!verdict) {
    return (
      <div className="rounded-xl p-5 bg-slate-100 border-2 border-slate-300 text-slate-600 text-center" data-testid="final-verdict-empty">
        Insufficient data for a final verdict yet.
      </div>
    );
  }

  const { top, second, third, margin, side, confidence, applied: isApplied, sumMin: sMin, sumMax: sMax, totalCandidates } = verdict;
  const isAbove = side === 'above';
  const conf = confidence === 'HIGH' ? { color: 'from-emerald-600 to-teal-700', badge: 'bg-emerald-400 text-emerald-900' }
            : confidence === 'MEDIUM' ? { color: 'from-blue-600 to-indigo-700', badge: 'bg-blue-300 text-blue-900' }
            : { color: 'from-slate-600 to-slate-700', badge: 'bg-slate-300 text-slate-900' };

  return (
    <div className={`rounded-xl overflow-hidden border-4 ${isAbove ? 'border-emerald-400' : 'border-rose-400'} shadow-2xl`} data-testid="final-verdict-block">
      <div className={`bg-gradient-to-r ${conf.color} px-5 py-3 text-white`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-black text-xl flex items-center gap-2">
              <span className="text-2xl">🎯</span>
              FINAL VERDICT — Most Likely Next Range
            </h3>
            <p className="text-[11px] text-white/85 mt-0.5">
              Unified across Master Voting (50%) + Streak Path Probability (35%) + Range Repetition (15%)
              {isApplied ? <> · <b className="text-amber-200">Filtered to your constraint {sMin}-{sMax}</b></> : <> · <b className="text-amber-200/80">No constraint applied — using all ranges</b></>}
            </p>
          </div>
          <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full ${conf.badge}`} data-testid="final-verdict-confidence">
            CONFIDENCE: {confidence}
          </span>
        </div>
      </div>

      <div className="bg-white p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`md:col-span-2 rounded-xl p-5 ${isAbove ? 'bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100 border-2 border-emerald-300' : 'bg-gradient-to-br from-rose-50 via-pink-50 to-rose-100 border-2 border-rose-300'}`} data-testid="final-verdict-pick">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-black opacity-60">📌 Pick #1 (top of {totalCandidates} eligible ranges)</span>
              <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${isAbove ? 'bg-emerald-200 text-emerald-900' : 'bg-rose-200 text-rose-900'}`}>
                {isAbove ? '⬆ ABOVE' : '⬇ BELOW'} avg ({avgSum})
              </span>
            </div>
            <div className={`text-6xl font-mono font-black text-center my-2 ${isAbove ? 'text-emerald-800' : 'text-rose-800'}`}>{top.range}</div>
            <div className="text-center text-[11px] text-slate-600">
              Unified score: <b className="font-mono text-base text-slate-900">{top.unified}</b>
              {second && <> · margin over #2 ({second.range}): <b className="font-mono text-slate-900">+{margin}</b></>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3 justify-center">
              {top.master_hits > 0 && (
                <span className="text-[10px] bg-blue-100 text-blue-900 px-2 py-1 rounded-full font-bold">
                  Sub-windows: <span className="font-mono">{top.master_hits} hits</span>
                </span>
              )}
              {top.streak_pct > 0 && (
                <span className="text-[10px] bg-violet-100 text-violet-900 px-2 py-1 rounded-full font-bold">
                  Streak prob: <span className="font-mono">{top.streak_pct}%</span>
                </span>
              )}
              {top.repetition_self > 0 && (
                <span className="text-[10px] bg-amber-100 text-amber-900 px-2 py-1 rounded-full font-bold">
                  Self-repeat: <span className="font-mono">{top.repetition_self}%</span>
                </span>
              )}
              {top.repetition_gap === 0 && (
                <span className="text-[10px] bg-emerald-200 text-emerald-900 px-2 py-1 rounded-full font-black">
                  ⚡ JUST APPEARED (gap = 0)
                </span>
              )}
              {top.is_missing_in_2026 && (
                <span className="text-[10px] bg-red-200 text-red-900 px-2 py-1 rounded-full font-black">
                  🔴 MISSING IN 2026
                </span>
              )}
            </div>
          </div>

          <div className="rounded-xl border-2 border-slate-200 p-3" data-testid="final-verdict-evidence">
            <div className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-2">Top-3 Eligible Ranges</div>
            <table className="w-full text-[10px]">
              <thead className="text-slate-500">
                <tr className="border-b">
                  <th className="text-left py-1 font-bold">#</th>
                  <th className="text-left py-1 font-bold">Range</th>
                  <th className="text-right py-1 font-bold">Score</th>
                </tr>
              </thead>
              <tbody>
                {[top, second, third].filter(Boolean).map((c, i) => (
                  <tr key={c.range} className={`border-b border-slate-100 ${i === 0 ? 'bg-amber-50' : ''}`}>
                    <td className="py-1.5 font-bold">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</td>
                    <td className="py-1.5 font-mono font-bold">{c.range}</td>
                    <td className="py-1.5 text-right font-mono font-black">{c.unified}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!isApplied && (
              <p className="text-[9px] text-amber-700 mt-2 italic">💡 Set your Min/Max constraint below to filter the verdict to your zone.</p>
            )}
          </div>
        </div>

        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-[11px] leading-relaxed text-slate-700" data-testid="final-verdict-reasoning">
          <b className="text-slate-900">Why {top.range}?</b>{' '}
          {top.master_hits >= 3 && <>Confirmed by {top.master_hits} sub-windows ({top.sources.slice(0, 4).join(', ')}). </>}
          {top.streak_pct >= 15 && <>Streak path probability is <b>{top.streak_pct}%</b> — strong signal. </>}
          {top.repetition_gap === 0 && <>Range just appeared on the most recent draw (gap = 0). </>}
          {top.is_missing_in_2026 && <>Range is missing from 2026 — overdue. </>}
          {top.repetition_self >= 12 && <>Self-repeat rate is <b>{top.repetition_self}%</b> — historically tends to recur. </>}
          {!isApplied && <>(No constraint filter applied — top pick is from all 24 sum ranges.)</>}
          {isApplied && <>(Filtered to your zone <b>{sMin}-{sMax}</b>.)</>}
        </div>
      </div>
    </div>
  );
}



/* ==========================================================================
 * STREAK PANEL — 22-year baseline for above/below-average streak context
 * Renders the current streak, all-time max streaks, per-year frequency of
 * 2-/3-/4-/5-streaks, and a next-draw reversion hint.
 * ========================================================================== */
function StreakPanel({ lineStreak, compact = false }) {
  if (!lineStreak) return null;
  const { avg, curSide, curLen, curEndSum, curEndDate, maxAbove, maxBelow, baselines, currentYear, nextSideHint } = lineStreak;

  // Rarity badge for the current streak
  const curBaselineKey = `streaks_of_${curLen}_per_year`;
  const curBaselineRate = baselines?.[curBaselineKey] ?? null;
  const rarity = curLen >= 7 ? 'EXTREMELY RARE' : curLen >= 5 ? 'RARE' : curLen >= 4 ? 'NOTABLE' : 'TYPICAL';
  const rarityColor = curLen >= 7 ? 'bg-red-600 text-white' : curLen >= 5 ? 'bg-orange-500 text-white' : curLen >= 4 ? 'bg-amber-400 text-amber-900' : 'bg-slate-200 text-slate-700';

  const isAbove = curSide === 'above';
  const sideTone = isAbove ? 'from-emerald-600 via-teal-600 to-emerald-700' : 'from-rose-600 via-pink-600 to-rose-700';

  return (
    <div className={`mt-4 rounded-xl overflow-hidden border-2 ${isAbove ? 'border-emerald-300' : 'border-rose-300'} bg-white shadow-sm`} data-testid="streak-panel">
      {/* Header */}
      <div className={`bg-gradient-to-r ${sideTone} px-4 py-3 text-white`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="font-black text-sm tracking-wide flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-white/25 rounded text-[10px] font-black tracking-wider">STREAK</span>
              Above / Below Lifetime Average ({avg})
            </h4>
            <p className="text-[11px] text-white/85 mt-0.5">
              Current: {curLen} draw{curLen === 1 ? '' : 's'} {isAbove ? 'ABOVE' : 'BELOW'} avg — ending {curEndDate} at sum {curEndSum}
            </p>
          </div>
          <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full ${rarityColor}`} data-testid="streak-rarity">{rarity}</span>
        </div>
      </div>

      {/* Top strip: current vs max */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 border-b">
        <div className={`rounded-lg p-3 text-center border-2 ${isAbove ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'}`} data-testid="streak-current">
          <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">Current streak</div>
          <div className={`text-3xl font-mono font-black ${isAbove ? 'text-emerald-800' : 'text-rose-800'}`}>{curLen}</div>
          <div className="text-[10px] font-bold mt-0.5">{isAbove ? '⬆ ABOVE' : '⬇ BELOW'}</div>
        </div>
        <div className="rounded-lg p-3 text-center bg-emerald-50 border border-emerald-200" data-testid="streak-max-above">
          <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700">All-time max ABOVE</div>
          <div className="text-3xl font-mono font-black text-emerald-800">{maxAbove}</div>
          <div className="text-[10px] text-emerald-700 mt-0.5">in {baselines.years_analyzed || 22} years</div>
        </div>
        <div className="rounded-lg p-3 text-center bg-rose-50 border border-rose-200" data-testid="streak-max-below">
          <div className="text-[9px] uppercase tracking-wider font-bold text-rose-700">All-time max BELOW</div>
          <div className="text-3xl font-mono font-black text-rose-800">{maxBelow}</div>
          <div className="text-[10px] text-rose-700 mt-0.5">in {baselines.years_analyzed || 22} years</div>
        </div>
        <div className={`rounded-lg p-3 text-center border-2 ${nextSideHint === 'above' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-400' : 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-400'}`} data-testid="streak-hint">
          <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">Next-draw hint</div>
          <div className="text-sm font-black mt-1">{nextSideHint === 'above' ? '⬆ GO ABOVE' : '⬇ GO BELOW'}</div>
          <div className="text-[9px] opacity-70 mt-0.5">{curLen >= (isAbove ? maxAbove : maxBelow) ? 'At historical max → reversion' : 'Below max → streak may continue'}</div>
        </div>
      </div>

      {!compact && (
        <div className="p-4 space-y-4">
          {/* 22-year baseline — how often each streak length happens */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              22-Year Baseline — Avg Streaks Per Year (across {baselines.years_analyzed || 22} complete years)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="streak-baseline-grid">
              {[
                { label: 'Length 2', key: 'streaks_of_2_per_year', accent: 'bg-slate-100 text-slate-800' },
                { label: 'Length 3', key: 'streaks_of_3_per_year', accent: 'bg-slate-100 text-slate-800' },
                { label: 'Length 4', key: 'streaks_of_4_per_year', accent: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300' },
                { label: 'Length 5', key: 'streaks_of_5_per_year', accent: 'bg-orange-100 text-orange-800 ring-1 ring-orange-300' },
                { label: 'Length 6', key: 'streaks_of_6_per_year', accent: 'bg-red-100 text-red-800 ring-1 ring-red-300' },
                { label: '≥7 (rare)', key: 'streaks_ge7_per_year', accent: 'bg-red-200 text-red-900 ring-1 ring-red-400' },
              ].map(b => (
                <div key={b.key} className={`rounded-lg p-2 text-center ${b.accent}`} data-testid={`baseline-${b.key}`}>
                  <div className="text-[9px] uppercase font-bold opacity-70">{b.label}</div>
                  <div className="text-xl font-mono font-black">{baselines[b.key] ?? '—'}</div>
                  <div className="text-[9px] opacity-70">per year</div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              Averages across all 22 complete years (2004–2025). A streak of length N counts once — e.g. a 5-draw run adds to &quot;Length 5&quot;, not to &quot;Length 2/3/4&quot;.
            </p>
          </div>

          {/* Current year state */}
          {currentYear?.year && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="streak-current-year">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1.5">
                {currentYear.year} (partial year) — streaks contained within year
              </div>
              <div className="flex flex-wrap gap-3 text-[11px]">
                <span className="px-2 py-1 bg-white rounded border border-emerald-200">
                  Above-runs: <b className="text-emerald-700 font-mono">{currentYear.above_runs}</b>
                </span>
                <span className="px-2 py-1 bg-white rounded border border-rose-200">
                  Below-runs: <b className="text-rose-700 font-mono">{currentYear.below_runs}</b>
                </span>
                <span className="px-2 py-1 bg-white rounded border border-emerald-200">
                  Max above: <b className="text-emerald-700 font-mono">{currentYear.max_above}</b>
                </span>
                <span className="px-2 py-1 bg-white rounded border border-rose-200">
                  Max below: <b className="text-rose-700 font-mono">{currentYear.max_below}</b>
                </span>
              </div>
            </div>
          )}

          {/* Interpretation */}
          <div className={`rounded-lg p-3 text-[11px] leading-relaxed ${isAbove ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-rose-50 border border-rose-200 text-rose-900'}`} data-testid="streak-interpretation">
            <b>Interpretation:</b> Current {curLen}-draw {isAbove ? 'above' : 'below'}-avg streak
            {curBaselineRate != null && ` — this length occurs on average ${curBaselineRate}x per year (22-yr base rate)`}.
            {' '}All-time max {isAbove ? 'above' : 'below'} streak is <b>{isAbove ? maxAbove : maxBelow}</b> draws.
            {curLen >= (isAbove ? maxAbove : maxBelow) ? (
              <> The current run has hit the historical ceiling — reversion to the opposite side is highly likely on the next draw.</>
            ) : curLen + 1 >= (isAbove ? maxAbove : maxBelow) ? (
              <> One more same-side draw would tie the all-time record — expect strong reversion pressure.</>
            ) : (
              <> Plenty of historical room to continue — the streak is still within typical bounds.</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



/* ==========================================================================
 * STREAK DEEP ANALYSIS — Dual-formula table (F1 long-term + F2 next-draw)
 * Renders Bounce 1-8 / Below 1-7 × 2 formulas, missing-ranges panel,
 * and a combined verdict cross-referencing overdue 2026 ranges.
 * ========================================================================== */
function StreakDeepAnalysis({ sda }) {
  if (!sda || !sda.series) return null;
  const { series, current, missing_ranges_2026: missingRanges, verdict, avg_reference: avgRef } = sda;

  // Find highest-scoring overdue rows for extra attention
  const sortedByF1 = [...series].sort((a, b) => (b.f1.score + b.f1.overdue * 0.1) - (a.f1.score + a.f1.overdue * 0.1));
  const sortedByF2 = [...series].sort((a, b) => b.f2.score - a.f2.score);
  const topF1Row = sortedByF1[0];
  const topF2Row = sortedByF2[0];

  const rowCls = (row) => {
    if (row.name === current?.name) return 'bg-yellow-50 ring-2 ring-yellow-400';
    if (row.name === topF1Row?.name || row.name === topF2Row?.name) return 'bg-emerald-50';
    if (row.f2.converges_with_missing) return 'bg-indigo-50';
    return '';
  };

  return (
    <div className="mt-4 rounded-xl overflow-hidden border-2 border-indigo-300 bg-white shadow-sm" data-testid="streak-deep-analysis">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-fuchsia-700 px-4 py-3 text-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="font-black text-sm tracking-wide flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-white/25 rounded text-[10px] font-black tracking-wider">DEEP ANALYSIS</span>
              Streak States × Next-Range Prediction
            </h4>
            <p className="text-[11px] text-white/85 mt-0.5">
              Formula 1 (long-term) vs Formula 2 (next-draw) for every Bounce 1-8 & Below 1-7 state, cross-referenced with 2026 missing ranges (avg ref: {avgRef})
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-bold">
            <span className="px-2 py-1 bg-yellow-400/90 text-yellow-900 rounded">⚡ CURRENT: {current?.name}</span>
          </div>
        </div>
      </div>

      {/* Verdict Block — Two-path decision tree */}
      {verdict && (
        <div className="p-4 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 border-b-2 border-violet-200" data-testid="sda-verdict">
          <div className="text-[10px] uppercase tracking-wider font-black text-violet-700 mb-2">
            🎯 Master Verdict — Next Draw Prediction (Two-Path Decision Tree)
          </div>

          {/* Top: Current state + final prediction */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="bg-white border-2 border-violet-300 rounded-lg p-3 text-center">
              <div className="text-[9px] uppercase font-bold text-violet-600">Current State</div>
              <div className="text-lg font-black text-violet-900 mt-0.5">{verdict.current_state}</div>
              <div className="text-[9px] text-violet-500">streak of {verdict.current_length} {verdict.current_side === 'above' ? '⬆ above' : '⬇ below'} avg</div>
            </div>
            <div className={`rounded-lg p-3 text-center border-2 ${verdict.predicted_side === 'above' ? 'bg-emerald-50 border-emerald-400' : 'bg-rose-50 border-rose-400'}`} data-testid="sda-verdict-prediction">
              <div className="text-[9px] uppercase font-bold opacity-70">Most-Likely Next Range</div>
              <div className="text-2xl font-black mt-0.5 font-mono">{verdict.predicted_range || '—'}</div>
              <div className="text-[10px] font-bold">{verdict.predicted_side === 'above' ? '⬆ ABOVE avg' : '⬇ BELOW avg'}</div>
            </div>
            <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-lg p-3 text-center text-white">
              <div className="text-[9px] uppercase font-bold opacity-80">Combined Probability</div>
              <div className="text-3xl font-mono font-black mt-0.5">{verdict.confidence_pct}%</div>
              <div className="text-[9px] opacity-75">F1: {verdict.f1_score} • F2: {verdict.f2_score}</div>
            </div>
          </div>

          {/* Two-path decision tree */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-testid="sda-two-paths">
            {/* Path A — BREAK */}
            <div className="rounded-lg overflow-hidden border-2 border-rose-300" data-testid="sda-path-break">
              <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white px-3 py-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[9px] uppercase font-bold opacity-80">PATH A — Streak BREAKS</div>
                    <div className="text-base font-black">→ flip to {verdict.path_break.side === 'above' ? '⬆ above' : '⬇ below'} avg</div>
                  </div>
                  <div className="text-2xl font-mono font-black">{verdict.path_break.probability_pct}%</div>
                </div>
              </div>
              <div className="bg-rose-50 p-3">
                <div className="text-[10px] uppercase font-bold text-rose-700 mb-1">Top next-ranges if breaks</div>
                <div className="space-y-1">
                  {verdict.path_break.top3.slice(0, 3).map((t, idx) => (
                    <div key={t.range} className="flex items-center justify-between bg-white rounded px-2 py-1 text-[11px]">
                      <span className="flex items-center gap-1.5">
                        <span className="w-4 text-rose-700 font-bold">{idx + 1}.</span>
                        <span className="font-mono font-black">{t.range}</span>
                      </span>
                      <span className="flex items-center gap-2 text-[10px]">
                        <span className="text-rose-700 font-bold">{t.pct}%</span>
                        <span className="text-slate-400">→ combined: {((verdict.path_break.probability_pct / 100) * t.pct).toFixed(1)}%</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Path B — CONTINUE */}
            <div className="rounded-lg overflow-hidden border-2 border-emerald-300" data-testid="sda-path-continue">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[9px] uppercase font-bold opacity-80">PATH B — Streak CONTINUES</div>
                    <div className="text-base font-black">→ stay {verdict.path_continue.side === 'above' ? '⬆ above' : '⬇ below'} avg</div>
                  </div>
                  <div className="text-2xl font-mono font-black">{verdict.path_continue.probability_pct}%</div>
                </div>
              </div>
              <div className="bg-emerald-50 p-3">
                <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Top next-ranges if continues</div>
                <div className="space-y-1">
                  {verdict.path_continue.top3.slice(0, 3).map((t, idx) => (
                    <div key={t.range} className="flex items-center justify-between bg-white rounded px-2 py-1 text-[11px]">
                      <span className="flex items-center gap-1.5">
                        <span className="w-4 text-emerald-700 font-bold">{idx + 1}.</span>
                        <span className="font-mono font-black">{t.range}</span>
                      </span>
                      <span className="flex items-center gap-2 text-[10px]">
                        <span className="text-emerald-700 font-bold">{t.pct}%</span>
                        <span className="text-slate-400">→ combined: {((verdict.path_continue.probability_pct / 100) * t.pct).toFixed(1)}%</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Combined top-3 row */}
          {verdict.combined_top3 && verdict.combined_top3.length > 0 && (
            <div className="bg-white border border-violet-200 rounded-lg p-3 mb-3" data-testid="sda-combined-top3">
              <div className="text-[10px] uppercase font-bold text-violet-700 mb-1.5">⭐ Combined Probability — Top 3 Most-Likely Next Ranges</div>
              <div className="grid grid-cols-3 gap-2">
                {verdict.combined_top3.map((t, idx) => (
                  <div key={t.range} className={`rounded-lg p-2 text-center border ${idx === 0 ? 'bg-violet-100 border-violet-400 ring-2 ring-violet-300' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="text-[9px] uppercase font-bold text-slate-500">#{idx + 1}</div>
                    <div className="text-base font-mono font-black">{t.range}</div>
                    <div className="text-[10px] font-bold text-violet-700">{t.pct}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-violet-900 leading-relaxed bg-white/60 rounded p-2 border border-violet-100" data-testid="sda-reasoning">
            <b>Reasoning:</b> {verdict.reasoning}
          </p>
        </div>
      )}

      {/* Missing Ranges Panel */}
      {missingRanges && missingRanges.length > 0 && (
        <div className="p-4 bg-amber-50 border-b border-amber-200" data-testid="sda-missing-ranges">
          <div className="text-[10px] uppercase tracking-wider font-black text-amber-800 mb-2">
            ⚠️ Ranges Missing From 2026 — Statistically Due
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {missingRanges.map(m => (
              <div key={m.range} className="bg-white border-2 border-amber-300 rounded-lg p-3" data-testid={`missing-${m.range}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-lg font-black text-amber-900">{m.range}</span>
                  <span className="text-[9px] uppercase font-bold bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">overdue score {m.overdue_score}</span>
                </div>
                <div className="text-[11px] text-amber-800 space-y-0.5">
                  <div>Historical: <b className="font-mono">{m.historical_pct}%</b> of all draws ({m.count_all} occurrences) — 5th-most common range ever.</div>
                  <div>2026 so far: <b className="font-mono text-red-700">0 occurrences</b> (pct_diff: {m.pct_diff}%, under-represented).</div>
                  <div>Last seen: <b className="font-mono">{m.last_seen_draws_ago} draws ago</b>.</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dual-Formula Table */}
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-black text-slate-600 mb-2">
            📊 Formula 1 (Long-term lens) • Formula 2 (Next-draw lens) — 15 Streak States
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-[10px]" data-testid="sda-table">
              <thead className="bg-slate-100 text-slate-700 sticky top-0">
                <tr>
                  <th colSpan={9} className="px-2 py-1.5 text-center font-black text-blue-700 bg-blue-50 border-b border-blue-200">FORMULA 1 — Long-term Frequency / Gap</th>
                  <th colSpan={8} className="px-2 py-1.5 text-center font-black text-emerald-700 bg-emerald-50 border-b border-emerald-200">FORMULA 2 — Next-draw Transition / Pattern</th>
                </tr>
                <tr>
                  <th className="px-2 py-1.5 text-left sticky left-0 bg-slate-100 z-10">Series</th>
                  <th className="px-2 py-1.5 text-right" title="Historical count of times this streak-state occurred and then broke">Count</th>
                  <th className="px-2 py-1.5 text-right" title="Share of all streak endings">Freq %</th>
                  <th className="px-2 py-1.5 text-right" title="Occurrences in 2026">2026</th>
                  <th className="px-2 py-1.5 text-right" title="2026 % minus historical % (negative = under-represented)">Diff %</th>
                  <th className="px-2 py-1.5 text-right" title="Expected gap between occurrences (1 ÷ freq)">Exp Gap</th>
                  <th className="px-2 py-1.5 text-right" title="Draws since this state last occurred">Cur Gap</th>
                  <th className="px-2 py-1.5 text-right" title="Cur Gap − Exp Gap (positive = overdue)">Overdue</th>
                  <th className="px-2 py-1.5 text-right font-bold bg-blue-50 text-blue-800" title="F1 composite score">F1</th>
                  <th className="px-2 py-1.5 text-left" title="Sum-range the NEXT draw historically lands in after this state">Next Range</th>
                  <th className="px-2 py-1.5 text-right" title="% of historical cases that transitioned to this range">Trs %</th>
                  <th className="px-2 py-1.5 text-right" title="Year this state most often occurred">Year</th>
                  <th className="px-2 py-1.5 text-right" title="Base rate (Count ÷ total draws)">Base</th>
                  <th className="px-2 py-1.5 text-right" title="Most common day-of-week this state ended on">DoW</th>
                  <th className="px-2 py-1.5 text-right" title="2nd-order probability (trans% boosted if converges with missing range)">M2 %</th>
                  <th className="px-2 py-1.5 text-right font-bold bg-emerald-50 text-emerald-800" title="F2 composite score">F2</th>
                  <th className="px-2 py-1.5 text-center" title="Does the top next-range coincide with a range missing in 2026?">Conv</th>
                </tr>
              </thead>
              <tbody>
                {series.map(row => {
                  const { f1, f2 } = row;
                  const isCurrent = row.name === current?.name;
                  return (
                    <tr key={row.name} className={`border-t border-slate-100 ${rowCls(row)}`} data-testid={`sda-row-${row.name.replace(' ', '-').toLowerCase()}`}>
                      <td className={`px-2 py-1.5 font-bold sticky left-0 bg-inherit ${row.side === 'above' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {isCurrent && <span className="inline-block mr-1 px-1 py-0.5 bg-yellow-400 text-yellow-900 rounded text-[8px] font-black">NOW</span>}
                        {row.name}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">{f1.count}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{f1.freq_pct}%</td>
                      <td className="px-2 py-1.5 text-right font-mono">{f1.count_2026}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${f1.diff_pct > 1 ? 'text-emerald-700' : f1.diff_pct < -1 ? 'text-rose-700' : 'text-slate-500'}`}>{f1.diff_pct > 0 ? '+' : ''}{f1.diff_pct}%</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{f1.exp_gap}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{f1.current_gap}</td>
                      <td className={`px-2 py-1.5 text-right font-mono font-bold ${f1.overdue > 30 ? 'text-rose-700' : f1.overdue > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{f1.overdue > 0 ? '+' : ''}{f1.overdue}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-black bg-blue-50 text-blue-900">{f1.score}</td>
                      <td className="px-2 py-1.5 text-left font-mono text-slate-700">{f2.range || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold">{f2.trans_pct}%</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{f2.year || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{f2.base}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{f2.dow || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{f2.m2}%</td>
                      <td className="px-2 py-1.5 text-right font-mono font-black bg-emerald-50 text-emerald-900">{f2.score}</td>
                      <td className="px-2 py-1.5 text-center">
                        {f2.converges_with_missing
                          ? <span className="inline-block px-1.5 py-0.5 bg-indigo-500 text-white rounded text-[8px] font-black">YES</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-[10px] text-slate-500 flex gap-4 flex-wrap">
            <span><span className="inline-block w-3 h-3 bg-yellow-200 ring-2 ring-yellow-400 align-middle mr-1"></span><b>NOW</b> = current streak state</span>
            <span><span className="inline-block w-3 h-3 bg-emerald-50 border align-middle mr-1"></span>Top F1 or F2 scored</span>
            <span><span className="inline-block w-3 h-3 bg-indigo-50 border align-middle mr-1"></span>Converges with a 2026 missing range</span>
          </div>
        </div>

        {/* === RANGE REPETITION ANALYSIS === */}
        {sda.range_repetition && sda.range_repetition.length > 0 && (
          <div data-testid="range-repetition-analysis">
            <div className="text-[10px] uppercase tracking-wider font-black text-slate-600 mb-2 mt-4">
              🔁 Range Repetition Analysis — How likely is each range to repeat?
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-[10px]" data-testid="range-rep-table">
                <thead className="bg-slate-100 text-slate-700 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Range</th>
                    <th className="px-2 py-1.5 text-right" title="Total occurrences in history">Count</th>
                    <th className="px-2 py-1.5 text-right" title="Share of all draws">Freq %</th>
                    <th className="px-2 py-1.5 text-right" title="% of times this range was followed by itself (consecutive repeat)">Self-Rpt %</th>
                    <th className="px-2 py-1.5 text-right" title="Average draws between successive occurrences">Avg Gap</th>
                    <th className="px-2 py-1.5 text-right" title="Draws since last occurrence">Cur Gap</th>
                    <th className="px-2 py-1.5 text-right" title="Longest historical streak of consecutive same-range draws">Max Con</th>
                    <th className="px-2 py-1.5 text-left" title="Most likely range to come AFTER this one">Next Top-3</th>
                    <th className="px-2 py-1.5 text-center" title="Position relative to lifetime average">Side</th>
                    <th className="px-2 py-1.5 text-center" title="Is this range missing from 2026 so far?">2026?</th>
                  </tr>
                </thead>
                <tbody>
                  {sda.range_repetition.map(r => {
                    // Highlight the range the current draw is in (current draw's range)
                    const isCurrentRangeOfLastDraw = (() => {
                      const last = current?.end_sum;
                      if (last == null) return false;
                      try {
                        const [lo, hi] = r.range.split('-').map(Number);
                        return last >= lo && last <= hi;
                      } catch { return false; }
                    })();
                    const cls = isCurrentRangeOfLastDraw
                      ? 'bg-yellow-50 ring-2 ring-yellow-400'
                      : r.is_missing_in_2026 ? 'bg-amber-50' : '';
                    return (
                      <tr key={r.range} className={`border-t border-slate-100 ${cls}`} data-testid={`range-rep-${r.range}`}>
                        <td className="px-2 py-1.5 font-mono font-black">
                          {isCurrentRangeOfLastDraw && <span className="inline-block mr-1 px-1 py-0.5 bg-yellow-400 text-yellow-900 rounded text-[8px] font-black">LAST</span>}
                          {r.range}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.count}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.freq_pct}%</td>
                        <td className={`px-2 py-1.5 text-right font-mono font-bold ${r.self_repeat_pct >= 12 ? 'text-emerald-700' : r.self_repeat_pct >= 8 ? 'text-amber-700' : 'text-slate-500'}`}>
                          {r.self_repeat_pct}%
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-500">{r.avg_gap}</td>
                        <td className={`px-2 py-1.5 text-right font-mono font-bold ${r.current_gap > r.avg_gap * 2 ? 'text-rose-700' : r.current_gap === 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                          {r.current_gap}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.max_consecutive}</td>
                        <td className="px-2 py-1.5 text-left text-[9px]">
                          {r.next_top3.slice(0, 3).map((t, idx) => (
                            <span key={t.range} className="inline-block mr-1.5">
                              <span className="font-mono">{t.range}</span>
                              <span className="text-slate-400">({t.pct}%)</span>
                              {idx < 2 && r.next_top3.length > idx + 1 ? <span className="text-slate-300"> ·</span> : null}
                            </span>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-black ${r.is_above_avg ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                            {r.is_above_avg ? '⬆ ABV' : '⬇ BLW'}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {r.is_missing_in_2026
                            ? <span className="inline-block px-1.5 py-0.5 bg-red-500 text-white rounded text-[8px] font-black">MISSING</span>
                            : <span className="text-slate-300">✓</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[10px] text-slate-500 flex gap-4 flex-wrap">
              <span><b>Self-Rpt %</b> ≥12% (green) means range frequently repeats consecutively.</span>
              <span><b>Cur Gap = 0</b> (green) means range JUST appeared on the most-recent draw.</span>
              <span><b>MISSING</b> (red) means 0 occurrences in 2026 so far.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
